import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useClaudeAI } from '../hooks/useClaudeAI';
import { useDialog } from '../components/AppDialog';
import ResumeEditor from '../components/ResumeEditor';
import ResumePreview from '../components/ResumePreview';
import { track } from '../utils/analytics';
import { TEMPLATES, CL_TEMPLATES, CV_TEMPLATES, SAMPLE_RESUME, SAMPLE_CL, EMPTY_RESUME, EMPTY_CL, EMPTY_CV } from '../data/samples';
import { exportToPDF, exportToTxt } from '../utils/exportUtils';
import { api } from '../utils/api';
import { applyProfileToDocument, clearProfileFromDocument, profileHasDetails } from '../utils/profile';
import { ArrowLeft, Pencil, Palette, LayoutTemplate, Eye, Sparkles, FileText, Save, Download, ChevronDown, Check, Loader2, Type, Layout, AlignLeft, RotateCcw, Target, Link2, ClipboardPaste, CheckCircle2, BarChart2, AlertTriangle, ChevronRight, RefreshCw, TrendingUp, ShieldCheck, Zap, Lock, Linkedin, Mail, Share2 } from 'lucide-react';
import RocketLoader from '../components/RocketLoader';
import DailyLimitModal from '../components/DailyLimitModal';

const EMPTIES = { resume: EMPTY_RESUME, cover_letter: EMPTY_CL, cv: EMPTY_CV };

const PREMIUM_SECTION_LABELS = {
  experience: 'Work Experience',
  education: 'Education',
  skills: 'Skills',
  projects: 'Projects',
  strengths: 'Key Strengths',
  goals: 'Career Goals',
  hobbies: 'Hobbies & Interests',
  achievements: 'Achievements',
  certifications: 'Certifications',
  awards: 'Awards',
  languages: 'Languages',
  volunteering: 'Volunteering',
  publications: 'Publications',
  references: 'References',
  customBlocks: 'Custom Blocks',
  personalInfo: 'Your Information',
  jobInfo: 'Job Information',
  content: 'Letter Content',
  signature: 'Closing & Signature',
  letterDate: 'Letter Date',
  openingGreeting: 'Opening Greeting',
  closingSalutation: 'Closing Salutation',
};

const getPremiumSectionLabel = (id) => (
  PREMIUM_SECTION_LABELS[id] ||
  id.replace(/([A-Z])/g, ' $1').replace(/^./, c => c.toUpperCase())
);

const ProfileAutofillBar = ({ applied, available, onApply, onRemove }) => {
  if (!available && !applied) return null;

  return (
    <div style={{
      margin: '0 0 12px',
      background: applied ? '#FFF7ED' : '#F8FAFC',
      border: `1px solid ${applied ? '#FED7AA' : '#E2E8F0'}`,
      borderRadius: 10,
      padding: '11px 12px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 12,
    }}>
      <div>
        <div style={{ fontSize: 12, fontWeight: 800, color: applied ? '#9A3412' : '#334155' }}>
          {applied ? 'Profile details added' : 'Saved profile available'}
        </div>
        <div style={{ fontSize: 11, color: applied ? '#C2410C' : '#64748B', marginTop: 2 }}>
          {applied ? 'Contact and profile fields were filled from Account & Profile.' : 'Fill contact fields from your saved Account & Profile details.'}
        </div>
      </div>
      {applied ? (
        <button onClick={onRemove} style={{ background: 'white', color: '#DC2626', border: '1px solid #FECACA', borderRadius: 7, padding: '7px 10px', fontSize: 11, fontWeight: 800, cursor: 'pointer', whiteSpace: 'nowrap' }}>
          Remove details
        </button>
      ) : (
        <button onClick={onApply} style={{ background: '#EF1D28', color: 'white', border: 'none', borderRadius: 7, padding: '7px 10px', fontSize: 11, fontWeight: 800, cursor: 'pointer', whiteSpace: 'nowrap' }}>
          Use profile
        </button>
      )}
    </div>
  );
};

const DESIGN_PRESETS = [
  { name: 'Mars Red',  accent: '#EF1D28', section: '#C5161D', body: '#1F2937', sidebar: '#FFF1E8', link: '#EF1D28' },
  { name: 'Full Blue', accent: '#2563EB', section: '#1D4ED8', body: '#1E293B', sidebar: '#EFF6FF', link: '#2563EB' },
  { name: 'Emerald',   accent: '#10B981', section: '#059669', body: '#1F2937', sidebar: '#ECFDF5', link: '#059669' },
  { name: 'Amber',     accent: '#F59E0B', section: '#D97706', body: '#1F2937', sidebar: '#FFFBEB', link: '#D97706' },
  { name: 'Rose',      accent: '#F87171', section: '#E11D48', body: '#1F2937', sidebar: '#FFF1F2', link: '#E11D48' },
  { name: 'Sky',       accent: '#38BDF8', section: '#0284C7', body: '#164E63', sidebar: '#F0F9FF', link: '#0284C7' },
  { name: 'Cool Gray', accent: '#64748B', section: '#475569', body: '#1E293B', sidebar: '#F8FAFC', link: '#475569' },
  { name: 'Taupe',     accent: '#A8A29E', section: '#78716C', body: '#292524', sidebar: '#FAFAF9', link: '#78716C' },
  { name: 'Pink',      accent: '#EC4899', section: '#DB2777', body: '#1F2937', sidebar: '#FDF2F8', link: '#DB2777' },
  { name: 'Silver',    accent: '#D4D4D8', section: '#71717A', body: '#27272A', sidebar: '#FAFAFA', link: '#71717A' },
  { name: 'Sunset',    accent: '#FB923C', section: '#EA580C', body: '#1F2937', sidebar: '#FFF7ED', link: '#F97316' },
  { name: 'Blue Red',  accent: '#2563EB', section: '#EF1D28', body: '#1E293B', sidebar: '#EFF6FF', link: '#EF1D28' },
  { name: 'Black',     accent: '#111111', section: '#374151', body: '#1F2937', sidebar: '#F9FAFB', link: '#374151' },
  { name: 'Violet',    accent: '#7C3AED', section: '#6D28D9', body: '#1F2937', sidebar: '#F5F3FF', link: '#6D28D9' },
  { name: 'Teal',      accent: '#0D9488', section: '#0F766E', body: '#1F2937', sidebar: '#F0FDFA', link: '#0F766E' },
  { name: 'Forest',    accent: '#166534', section: '#15803D', body: '#1F2937', sidebar: '#F0FDF4', link: '#15803D' },
  { name: 'Indigo',    accent: '#4338CA', section: '#3730A3', body: '#1F2937', sidebar: '#EEF2FF', link: '#3730A3' },
  { name: 'Dark Navy', accent: '#0F172A', section: '#1E3A5F', body: '#0F172A', sidebar: '#F8FAFC', link: '#1E3A5F' },
  { name: 'Burgundy', accent: '#7F1D1D', section: '#991B1B', body: '#1F2937', sidebar: '#FEF2F2', link: '#991B1B' },
  { name: 'Plum',     accent: '#581C87', section: '#6B21A8', body: '#1F2937', sidebar: '#FAF5FF', link: '#6B21A8' },
  { name: 'Cyan',     accent: '#06B6D4', section: '#0891B2', body: '#1F2937', sidebar: '#ECFEFF', link: '#0891B2' },
  { name: 'Olive',    accent: '#65A30D', section: '#4D7C0F', body: '#1F2937', sidebar: '#F7FEE7', link: '#4D7C0F' },
  { name: 'Fuchsia',  accent: '#D946EF', section: '#C026D3', body: '#1F2937', sidebar: '#FDF4FF', link: '#C026D3' },
  { name: 'Brown',    accent: '#78350F', section: '#92400E', body: '#1F2937', sidebar: '#FFF7ED', link: '#92400E' },
  { name: 'Lime',     accent: '#84CC16', section: '#65A30D', body: '#1F2937', sidebar: '#F7FEE7', link: '#65A30D' },
  { name: 'Navy Teal',accent: '#0E7490', section: '#0C4A6E', body: '#0C4A6E', sidebar: '#F0F9FF', link: '#0C4A6E' },
  { name: 'Crimson',  accent: '#BE123C', section: '#9F1239', body: '#1F2937', sidebar: '#FFF1F2', link: '#9F1239' },
  { name: 'Slate',    accent: '#334155', section: '#1E293B', body: '#0F172A', sidebar: '#F8FAFC', link: '#1E293B' },
  { name: 'Peach',    accent: '#FB7185', section: '#F43F5E', body: '#1F2937', sidebar: '#FFF1F2', link: '#F43F5E' },
  { name: 'Gold',     accent: '#CA8A04', section: '#A16207', body: '#1F2937', sidebar: '#FEFCE8', link: '#A16207' },
  { name: 'Terracotta', accent: '#C2541C', section: '#9A3412', body: '#1C1917', sidebar: '#FFF7ED', link: '#C2541C' },
  { name: 'Copper',     accent: '#B45309', section: '#92400E', body: '#1C1917', sidebar: '#FFFBEB', link: '#B45309' },
  { name: 'Champagne',  accent: '#A37E2C', section: '#8A6A24', body: '#1C1917', sidebar: '#FBF7EB', link: '#A37E2C' },
  { name: 'Rose Gold',  accent: '#9F3054', section: '#822644', body: '#1C1917', sidebar: '#FFF1F5', link: '#9F3054' },
  { name: 'Coral',      accent: '#FF6F61', section: '#E4574A', body: '#1F2937', sidebar: '#FFF1F0', link: '#E4574A' },
  { name: 'Berry',      accent: '#86198F', section: '#701A75', body: '#1F2937', sidebar: '#FDF4FF', link: '#86198F' },
  { name: 'Lavender',   accent: '#A78BFA', section: '#7C3AED', body: '#1F2937', sidebar: '#F5F3FF', link: '#7C3AED' },
  { name: 'Midnight',   accent: '#312E81', section: '#1E1B4B', body: '#1E293B', sidebar: '#EEF2FF', link: '#312E81' },
  { name: 'Ocean',      accent: '#0369A1', section: '#075985', body: '#0F172A', sidebar: '#F0F9FF', link: '#0369A1' },
  { name: 'Steel',      accent: '#4682B4', section: '#36648B', body: '#1E293B', sidebar: '#F0F6FA', link: '#36648B' },
  { name: 'Pine',       accent: '#134E4A', section: '#0F3D3A', body: '#1F2937', sidebar: '#F0FDFA', link: '#134E4A' },
  { name: 'Nordic',     accent: '#1C2B39', section: '#16222D', body: '#1F2937', sidebar: '#F8FAFC', link: '#1C2B39' },
];

// Color math for the "any color" custom theme
const shadeHex = (hex, pct) => {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex || '');
  if (!m) return hex;
  const n = parseInt(m[1], 16);
  const ch = (s) => Math.max(0, Math.min(255, Math.round(((n >> s) & 0xff) * (1 + pct))));
  return `#${((ch(16) << 16) | (ch(8) << 8) | ch(0)).toString(16).padStart(6, '0')}`;
};
const tintHex = (hex, t) => {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex || '');
  if (!m) return hex;
  const n = parseInt(m[1], 16);
  const ch = (s) => { const v = (n >> s) & 0xff; return Math.round(v + (255 - v) * t); };
  return `#${((ch(16) << 16) | (ch(8) << 8) | ch(0)).toString(16).padStart(6, '0')}`;
};

const TabBtn = ({ active, onClick, icon: Icon, children }) => (
  <button onClick={onClick} style={{
    display: 'flex', alignItems: 'center', gap: 6,
    padding: '0 16px', height: 64, border: 'none',
    borderBottom: active ? '2.5px solid #EF1D28' : '2.5px solid transparent',
    background: 'transparent',
    color: active ? '#EF1D28' : '#6B7280',
    fontSize: 13, fontWeight: active ? 700 : 500,
    cursor: 'pointer', transition: 'color 0.15s, border-color 0.15s',
    whiteSpace: 'nowrap', fontFamily: 'Inter, sans-serif',
    marginBottom: -1,
  }}
  onMouseEnter={e => { if (!active) e.currentTarget.style.color = '#374151'; }}
  onMouseLeave={e => { if (!active) e.currentTarget.style.color = '#6B7280'; }}>
    {Icon && <Icon size={14} strokeWidth={active ? 2.4 : 1.8} />}
    {children}
  </button>
);

// ── Design Panel shared sub-components (MUST be module-level, not inside DesignPanel) ──
const DPSectionCard = ({ title, icon: Icon, children }) => {
  const [open, setOpen] = React.useState(true);
  return (
    <div style={{ background: 'white', border: '1px solid #E5E7EB', borderRadius: 12, marginBottom: 10, overflow: 'hidden' }}>
      <div onClick={() => setOpen(p => !p)} style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '11px 14px', cursor: 'pointer', userSelect: 'none' }}>
        {Icon && <Icon size={13} color={open ? '#EF1D28' : '#9CA3AF'} strokeWidth={2.2} />}
        <span style={{ flex: 1, fontSize: 12, fontWeight: 700, color: '#111827', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{title}</span>
        <ChevronDown size={14} color="#9CA3AF" style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
      </div>
      {open && <div style={{ padding: '4px 14px 16px', borderTop: '1px solid #F3F4F6' }}>{children}</div>}
    </div>
  );
};

const DPRow = ({ label, value, children }) => (
  <div style={{ marginTop: 13 }}>
    <div style={{ fontSize: 11, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 7 }}>
      {label}{value !== undefined && <span style={{ marginLeft: 6, color: '#111827', textTransform: 'none', letterSpacing: 0 }}>{value}</span>}
    </div>
    {children}
  </div>
);

const DPChoiceGrid = ({ options, current, onSelect, cols = 2 }) => (
  <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: 6 }}>
    {options.map(({ value, label }) => {
      const active = current === value;
      return (
        <button key={value} type="button" onClick={() => onSelect(value)} style={{
          height: 34, border: `1.5px solid ${active ? '#EF1D28' : '#E5E7EB'}`, borderRadius: 8,
          background: active ? '#FFF1E8' : '#FAFAFA', color: active ? '#EF1D28' : '#374151',
          fontWeight: active ? 700 : 500, fontSize: 12, cursor: 'pointer', fontFamily: 'Inter, sans-serif',
          transition: 'all 0.12s',
        }}>{label}</button>
      );
    })}
  </div>
);

const DPToggle = ({ checked, onChange: onToggle }) => (
  <button type="button" onClick={() => onToggle(!checked)} style={{
    width: 36, height: 20, borderRadius: 20, border: 'none', padding: 3,
    background: checked ? '#EF1D28' : '#E5E7EB', cursor: 'pointer',
    display: 'flex', justifyContent: checked ? 'flex-end' : 'flex-start', alignItems: 'center',
    transition: 'background 0.2s', flexShrink: 0,
  }}>
    <span style={{ width: 14, height: 14, borderRadius: '50%', background: 'white', display: 'block', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
  </button>
);

const DPToggleRow = ({ label, sub, checked, onChange: onToggle }) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 13, gap: 8 }}>
    <div>
      <div style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>{label}</div>
      {sub && <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 1 }}>{sub}</div>}
    </div>
    <DPToggle checked={checked} onChange={onToggle} />
  </div>
);

const DP_SELECT = { width: '100%', height: 36, padding: '0 10px', border: '1.5px solid #E5E7EB', borderRadius: 8, background: 'white', color: '#111827', fontSize: 12, fontWeight: 500, outline: 'none', fontFamily: 'inherit', cursor: 'pointer' };
const DP_FREE = <span style={{ marginLeft: 5, background: '#D1FAE5', color: '#065F46', borderRadius: 4, padding: '1px 5px', fontSize: 9, fontWeight: 800, letterSpacing: 0.3 }}>FREE</span>;

const DP_RANGE_CSS = `
  .dp-range { width: 100%; height: 24px; appearance: none; background: transparent; outline: none; cursor: pointer; }
  .dp-range::-webkit-slider-runnable-track { height: 4px; border-radius: 999px; background: #E5E7EB; }
  .dp-range::-webkit-slider-thumb { appearance: none; width: 18px; height: 18px; margin-top: -7px; border-radius: 50%; background: #EF1D28; border: 2px solid white; box-shadow: 0 1px 4px rgba(0,0,0,0.2); cursor: pointer; }
  .dp-range::-moz-range-track { height: 4px; border-radius: 999px; background: #E5E7EB; }
  .dp-range::-moz-range-thumb { width: 18px; height: 18px; border-radius: 50%; background: #EF1D28; border: 2px solid white; cursor: pointer; }
`;

// ── Design Panel ──────────────────────────────────────────────────────────────
const CLDesignPanel = ({ data, onChange }) => {
  const pi = data?.personalInfo || {};
  const setDesign = patch => onChange({ ...data, personalInfo: { ...pi, ...patch } });
  return (
    <div style={{ padding: '14px 12px 28px', background: '#F9FAFB', minHeight: '100%' }}>
      <style>{DP_RANGE_CSS}</style>
      <DPSectionCard title="Color Theme" icon={Palette}>
        <DPRow label="Accent Color">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
            <input type="color" value={pi.accentColor || '#1E3A5F'} onChange={e => setDesign({ accentColor: e.target.value })}
              style={{ width: 36, height: 36, border: '1.5px solid #E5E7EB', borderRadius: 8, cursor: 'pointer', padding: 2, flexShrink: 0 }} />
            <input value={pi.accentColor || ''} onChange={e => setDesign({ accentColor: e.target.value })} placeholder="#1E3A5F"
              style={{ ...DP_SELECT, height: 36, flex: 1 }} />
          </div>
        </DPRow>
        <DPRow label="Text Color">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
            <input type="color" value={pi.bodyColor || '#1F2937'} onChange={e => setDesign({ bodyColor: e.target.value })}
              style={{ width: 36, height: 36, border: '1.5px solid #E5E7EB', borderRadius: 8, cursor: 'pointer', padding: 2, flexShrink: 0 }} />
            <input value={pi.bodyColor || ''} onChange={e => setDesign({ bodyColor: e.target.value || undefined })} placeholder="Default"
              style={{ ...DP_SELECT, height: 36, flex: 1 }} />
          </div>
        </DPRow>
      </DPSectionCard>

      <DPSectionCard title="Typography" icon={Type}>
        <DPRow label="Font">
          <select value={pi.fontFamily || 'template'} onChange={e => setDesign({ fontFamily: e.target.value })} style={DP_SELECT}>
            <option value="template">Template default</option>
            <optgroup label="Sans-serif">
              <option value="inter">Inter</option>
              <option value="modern">Segoe UI</option>
              <option value="clean">Helvetica Neue</option>
              <option value="roboto">Roboto</option>
              <option value="lato">Lato</option>
              <option value="opensans">Open Sans</option>
            </optgroup>
            <optgroup label="Serif">
              <option value="classic">Times New Roman</option>
              <option value="elegant">Garamond</option>
              <option value="merriweather">Merriweather</option>
              <option value="playfair">Playfair Display</option>
            </optgroup>
          </select>
        </DPRow>
        <DPRow label="Font Size">
          <DPChoiceGrid cols={4}
            options={[{value:'compact',label:'S'},{value:'normal',label:'M'},{value:'large',label:'L'},{value:'xl',label:'XL'}]}
            current={pi.fontSizeMode || 'normal'}
            onSelect={v => setDesign({ fontSizeMode: v })} />
        </DPRow>
        <DPRow label="Line Spacing" value={pi.lineSpacing ? `${pi.lineSpacing}px` : 'Auto'}>
          <input className="dp-range" type="range" min={14} max={26} value={Number(pi.lineSpacing || 22)} onChange={e => setDesign({ lineSpacing: Number(e.target.value) })} />
        </DPRow>
      </DPSectionCard>

      <DPSectionCard title="Layout" icon={Layout}>
        <DPRow label="Page Size">
          <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
            {[['a4','A4',94],['letter','Letter',86]].map(([val, lab, h]) => {
              const active = (pi.pageSize || 'a4') === val;
              return (
                <button key={val} type="button" onClick={() => setDesign({ pageSize: val })} style={{
                  width: 64, height: h, border: `2px solid ${active ? '#EF1D28' : '#E5E7EB'}`,
                  background: active ? '#FFF1E8' : '#FAFAFA', borderRadius: 8,
                  color: active ? '#EF1D28' : '#374151', fontSize: 12, fontWeight: 700, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.12s',
                  boxShadow: active ? '0 0 0 3px rgba(239,29,40,0.12)' : 'none',
                }}>{lab}</button>
              );
            })}
          </div>
        </DPRow>
        <DPRow label="Margins">
          <DPChoiceGrid cols={3}
            options={[{value:'compact',label:'Compact'},{value:'normal',label:'Normal'},{value:'relaxed',label:'Relaxed'}]}
            current={pi.margins || 'normal'}
            onSelect={v => setDesign({ margins: v })} />
        </DPRow>
      </DPSectionCard>

      <button type="button" onClick={() => {
        onChange({ ...data, personalInfo: { ...pi, accentColor: undefined, bodyColor: undefined, fontFamily: undefined, fontSizeMode: undefined, lineSpacing: undefined, pageSize: undefined, margins: undefined } });
      }} style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'white', color: '#6B7280', border: '1px solid #E5E7EB', borderRadius: 8, padding: '9px 16px', fontSize: 12, fontWeight: 600, cursor: 'pointer', marginTop: 4, fontFamily: 'Inter, sans-serif' }}
        onMouseEnter={e => { e.currentTarget.style.borderColor = '#EF1D28'; e.currentTarget.style.color = '#EF1D28'; }}
        onMouseLeave={e => { e.currentTarget.style.borderColor = '#E5E7EB'; e.currentTarget.style.color = '#6B7280'; }}>
        <RotateCcw size={13} /> Reset to defaults
      </button>
    </div>
  );
};

const PHOTO_TEMPLATES = new Set([
  'modern','classic','executive','minimal','creative','tech','elegant','corporate',
  'navy_timeline','europass','blue_analyst','gregory_sidebar','academic','academic_modern',
  'slate_sidebar','teal_banner',
  'amber_executive','violet_sidebar','bold_stripe','rose_gold','forest_pro',
  'sky_pro','charcoal_split','terracotta','startup_clean','champagne',
  'editorial','sage','graphite',
]);
// Templates whose page/sidebar background is driven by pi.sidebarColor
const BG_COLOR_TEMPLATES = {
  modern: 'Sidebar Color', slate_sidebar: 'Sidebar Color', ivory_serif: 'Background Color',
  charcoal_split: 'Sidebar Color', violet_sidebar: 'Sidebar Color', rose_gold: 'Sidebar Color',
  gregory_sidebar: 'Sidebar Color', academic_modern: 'Sidebar Color',
  cambridge_blue: 'Sidebar Color', emerald_academic: 'Sidebar Color',
  sage: 'Background Color',
  clinical_teal: 'Sidebar Color',
  estate_sidebar: 'Sidebar Color',
  sentinel_dark:  'Sidebar Color',
  bloom_sidebar:  'Sidebar Color',
};
const DIVIDER_TEMPLATES = new Set([
  'modern','classic','executive','creative','corporate','blue_corporate','navy_timeline',
  'harvard_standard','blue_analyst','academic','academic_modern','slate_sidebar','mono_pro',
  'ivory_serif','teal_banner','swiss_grid',
  'amber_executive','violet_sidebar','bold_stripe','rose_gold','forest_pro',
  'sky_pro','charcoal_split','terracotta','startup_clean','champagne',
  'editorial','sage','graphite',
  'med_pro_blue','nurse_rx','healthcare_pro',
]);
const SKILLS_TAGS_TEMPLATES = new Set([
  'modern','classic','executive','creative','tech','elegant','corporate','academic',
  'slate_sidebar','mono_pro','ivory_serif','teal_banner','swiss_grid',
  'amber_executive','bold_stripe','rose_gold','forest_pro',
  'sky_pro','charcoal_split','terracotta','startup_clean','champagne',
  'editorial','sage','graphite',
  'med_pro_blue','clinical_teal','nurse_rx','healthcare_pro','critical_care',
  'realty_pro','estate_sidebar','luxury_broker','re_corporate','property_exec',
  'shield_pro','patrol_blue','sentinel_dark','guardian_grid','tactical_clean',
  'exec_suite','boardroom','bizdev_pro','strategy_blue','c_suite',
  'bloom_sidebar','salon_chic','spa_zen','glow_pro','beauty_badge',
]);

const DesignPanel = ({ data, onChange }) => {
  const pi = data?.personalInfo || {};
  const tpl = data?.template || '';
  const setDesign = patch => onChange({ ...data, personalInfo: { ...pi, ...patch } });

  const activePreset = p =>
    (pi.accentColor || '') === p.accent &&
    (pi.sectionColor || '') === p.section &&
    (pi.sidebarColor || '') === p.sidebar;
  const activeColor = DESIGN_PRESETS.find(activePreset);
  const fontMode = pi.fontSizeMode || 'normal';
  const lineSpacing = Number(pi.lineSpacing || 18);

  return (
    <div style={{ padding: '14px 12px 28px', background: '#F9FAFB', minHeight: '100%' }}>
      <style>{DP_RANGE_CSS}</style>

      {/* ── Color Theme ── */}
      <DPSectionCard title="Color Theme" icon={Palette}>
        <DPRow label="Preset" value={activeColor ? activeColor.name : 'Custom'}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6,1fr)', gap: 7 }}>
            {DESIGN_PRESETS.map((preset, i) => {
              const active = activePreset(preset);
              return (
                <button key={i} type="button"
                  onClick={() => setDesign({ accentColor: preset.accent, nameColor: undefined, sectionColor: preset.section, bodyColor: preset.body, sidebarColor: preset.sidebar, linkColor: preset.link })}
                  title={preset.name}
                  style={{
                    width: '100%', aspectRatio: '1', padding: 0, border: `2px solid ${active ? '#EF1D28' : 'transparent'}`,
                    borderRadius: 8, cursor: 'pointer', overflow: 'hidden',
                    background: `linear-gradient(135deg, ${preset.accent} 0%, ${preset.accent} 40%, ${preset.body || '#1F2937'} 40%, ${preset.body || '#1F2937'} 70%, ${preset.sidebar || '#F9FAFB'} 70%)`,
                    boxShadow: active ? '0 0 0 3px rgba(239,29,40,0.2)' : '0 1px 3px rgba(0,0,0,0.1)',
                    transition: 'all 0.15s',
                  }} />
              );
            })}
            {/* Any color: opens the full color palette and builds a matching theme */}
            <div title="Pick any color…" style={{
              position: 'relative', width: '100%', aspectRatio: '1', borderRadius: 8, overflow: 'hidden',
              border: `2px solid ${activeColor ? 'transparent' : '#EF1D28'}`,
              background: 'conic-gradient(from 200deg, #EF4444, #F59E0B, #84CC16, #10B981, #06B6D4, #3B82F6, #8B5CF6, #EC4899, #EF4444)',
              boxShadow: activeColor ? '0 1px 3px rgba(0,0,0,0.1)' : '0 0 0 3px rgba(239,29,40,0.2)',
            }}>
              <span style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: 15, fontWeight: 900, textShadow: '0 1px 3px rgba(0,0,0,0.5)', pointerEvents: 'none' }}>+</span>
              <input type="color" aria-label="Pick any color" value={pi.accentColor || '#EF1D28'}
                onChange={e => {
                  const hex = e.target.value;
                  setDesign({ accentColor: hex, sectionColor: shadeHex(hex, -0.18), bodyColor: '#1F2937', sidebarColor: tintHex(hex, 0.92), linkColor: hex, nameColor: undefined });
                }}
                style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer', padding: 0, border: 'none' }} />
            </div>
          </div>
        </DPRow>

        {/* Custom color pickers */}
        <div style={{ marginTop: 13, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          {/* Accent */}
          <div>
            <div style={{ fontSize: 10, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 5 }}>Accent</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <input type="color" value={pi.accentColor || '#EF1D28'}
                onChange={e => setDesign({ accentColor: e.target.value })}
                style={{ width: 32, height: 32, border: '1.5px solid #E5E7EB', borderRadius: 7, cursor: 'pointer', padding: 2, flexShrink: 0 }} />
              <input value={pi.accentColor || ''} onChange={e => setDesign({ accentColor: e.target.value || undefined })} placeholder="Default"
                style={{ ...DP_SELECT, height: 32, flex: 1, fontSize: 11 }} />
            </div>
          </div>
          {/* Body / Text */}
          <div>
            <div style={{ fontSize: 10, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 5 }}>Text Color</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <input type="color" value={pi.bodyColor || '#1F2937'}
                onChange={e => setDesign({ bodyColor: e.target.value })}
                style={{ width: 32, height: 32, border: '1.5px solid #E5E7EB', borderRadius: 7, cursor: 'pointer', padding: 2, flexShrink: 0 }} />
              <input value={pi.bodyColor || ''} onChange={e => setDesign({ bodyColor: e.target.value || undefined })} placeholder="Default"
                style={{ ...DP_SELECT, height: 32, flex: 1, fontSize: 11 }} />
            </div>
          </div>
          {/* Link */}
          <div>
            <div style={{ fontSize: 10, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 5 }}>Link Color</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <input type="color" value={pi.linkColor || pi.sectionColor || pi.accentColor || '#EF1D28'}
                onChange={e => setDesign({ linkColor: e.target.value })}
                style={{ width: 32, height: 32, border: '1.5px solid #E5E7EB', borderRadius: 7, cursor: 'pointer', padding: 2, flexShrink: 0 }} />
              <input value={pi.linkColor || ''} onChange={e => setDesign({ linkColor: e.target.value || undefined })} placeholder="Auto"
                style={{ ...DP_SELECT, height: 32, flex: 1, fontSize: 11 }} />
            </div>
          </div>
        </div>

        <DPRow label="Section Heading Color">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
            <input type="color" value={pi.sectionColor || pi.accentColor || '#EF1D28'}
              onChange={e => setDesign({ sectionColor: e.target.value })}
              style={{ width: 32, height: 32, border: '1.5px solid #E5E7EB', borderRadius: 7, cursor: 'pointer', padding: 2, flexShrink: 0 }} />
            <input value={pi.sectionColor || ''} onChange={e => setDesign({ sectionColor: e.target.value || undefined })} placeholder="Same as accent"
              style={{ ...DP_SELECT, height: 32, flex: 1 }} />
          </div>
        </DPRow>

        {BG_COLOR_TEMPLATES[tpl] && (
          <DPRow label={BG_COLOR_TEMPLATES[tpl]}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
              <input type="color" value={pi.sidebarColor || '#FFFDF8'} onChange={e => setDesign({ sidebarColor: e.target.value })}
                style={{ width: 32, height: 32, border: '1.5px solid #E5E7EB', borderRadius: 7, cursor: 'pointer', padding: 2, flexShrink: 0 }} />
              <input value={pi.sidebarColor || ''} onChange={e => setDesign({ sidebarColor: e.target.value || undefined })} placeholder="Template default"
                style={{ ...DP_SELECT, height: 32, flex: 1 }} />
            </div>
          </DPRow>
        )}

      </DPSectionCard>

      {/* ── Typography ── */}
      <DPSectionCard title="Typography" icon={Type}>
        <DPRow label={<>Font {DP_FREE}</>}>
          <select value={pi.fontFamily || 'template'} onChange={e => setDesign({ fontFamily: e.target.value })} style={DP_SELECT}>
            <option value="template">Template default</option>
            <optgroup label="Sans-serif">
              <option value="inter">Inter</option>
              <option value="modern">Segoe UI</option>
              <option value="clean">Helvetica Neue</option>
              <option value="roboto">Roboto</option>
              <option value="lato">Lato</option>
              <option value="opensans">Open Sans</option>
              <option value="raleway">Raleway</option>
              <option value="nunito">Nunito</option>
              <option value="sourcesans">Source Sans 3</option>
            </optgroup>
            <optgroup label="Serif">
              <option value="classic">Times New Roman</option>
              <option value="elegant">Garamond</option>
              <option value="merriweather">Merriweather</option>
              <option value="playfair">Playfair Display</option>
            </optgroup>
            <optgroup label="Monospace">
              <option value="ibm">IBM Plex Mono</option>
              <option value="mono">Courier New</option>
            </optgroup>
          </select>
        </DPRow>

        <DPRow label="Font Size">
          <DPChoiceGrid cols={4}
            options={[{value:'compact',label:'S'},{value:'normal',label:'M'},{value:'large',label:'L'},{value:'xl',label:'XL'}]}
            current={fontMode}
            onSelect={v => setDesign({ fontSizeMode: v })} />
        </DPRow>

        <DPRow label="Line Spacing" value={`${lineSpacing}px`}>
          <input className="dp-range" type="range" min={14} max={26} value={lineSpacing} onChange={e => setDesign({ lineSpacing: Number(e.target.value) })} />
        </DPRow>

        <DPRow label="Heading Case">
          <DPChoiceGrid cols={2}
            options={[{value:'custom',label:'Aa  Title Case'},{value:'uppercase',label:'AA  ALL CAPS'}]}
            current={pi.sectionCase || 'uppercase'}
            onSelect={v => setDesign({ sectionCase: v })} />
        </DPRow>

        <DPRow label="Bullet Symbol">
          <DPChoiceGrid cols={4}
            options={[{value:'•',label:'•'},{value:'-',label:'—'},{value:'›',label:'›'},{value:'▸',label:'▸'}]}
            current={pi.bulletSymbol || '•'}
            onSelect={v => setDesign({ bulletSymbol: v })} />
        </DPRow>
      </DPSectionCard>

      {/* ── Layout ── */}
      <DPSectionCard title="Layout" icon={Layout}>
        <DPRow label="Page Size">
          <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
            {[['a4','A4',94],['letter','Letter',86]].map(([val, lab, h]) => {
              const active = (pi.pageSize || 'a4') === val;
              return (
                <button key={val} type="button" onClick={() => setDesign({ pageSize: val })} style={{
                  width: 64, height: h, border: `2px solid ${active ? '#EF1D28' : '#E5E7EB'}`,
                  background: active ? '#FFF1E8' : '#FAFAFA', borderRadius: 8,
                  color: active ? '#EF1D28' : '#374151', fontSize: 12, fontWeight: 700, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.12s',
                  boxShadow: active ? '0 0 0 3px rgba(239,29,40,0.12)' : 'none',
                }}>
                  {lab}
                </button>
              );
            })}
          </div>
        </DPRow>

        <DPRow label="Margins">
          <DPChoiceGrid cols={3}
            options={[{value:'compact',label:'Compact'},{value:'normal',label:'Normal'},{value:'relaxed',label:'Relaxed'}]}
            current={pi.margins || 'normal'}
            onSelect={v => setDesign({ margins: v })} />
        </DPRow>

        <DPToggleRow label="Page Numbers" sub="Show at bottom of each page"
          checked={!!pi.pageNumberVisible} onChange={checked => setDesign({ pageNumberVisible: checked })} />

        {PHOTO_TEMPLATES.has(tpl) && (
          <DPToggleRow label="Show Photo"
            sub="Display profile photo in header"
            checked={pi.showPhoto !== false} onChange={checked => setDesign({ showPhoto: checked })} />
        )}
      </DPSectionCard>

      {/* ── Formatting ── */}
      <DPSectionCard title="Formatting" icon={AlignLeft}>
        <DPRow label="Date Format">
          <select value={pi.dateFormat || 'shortMonth'} onChange={e => setDesign({ dateFormat: e.target.value })} style={DP_SELECT}>
            <option value="shortMonth">Jan 2026</option>
            <option value="longMonth">January 2026</option>
            <option value="numeric">01/2026</option>
            <option value="year">2026</option>
          </select>
        </DPRow>

        <DPRow label="Address Format">
          <select value={pi.addressFormat || 'western'} onChange={e => setDesign({ addressFormat: e.target.value })} style={DP_SELECT}>
            <option value="western">Full Name, Address, City</option>
            <option value="compact">Address, City</option>
            <option value="cityFirst">City, Country only</option>
          </select>
        </DPRow>

        {DIVIDER_TEMPLATES.has(tpl) && (
          <DPRow label="Section Divider">
            <DPChoiceGrid cols={3}
              options={[{value:'line',label:'Line'},{value:'dots',label:'· · ·'},{value:'none',label:'None'}]}
              current={pi.sectionDivider || 'line'}
              onSelect={v => setDesign({ sectionDivider: v })} />
          </DPRow>
        )}

        {SKILLS_TAGS_TEMPLATES.has(tpl) && (
          <DPToggleRow label="Skills as Tags" sub="Show skills as pill badges"
            checked={!!pi.skillsAsTags} onChange={checked => setDesign({ skillsAsTags: checked })} />
        )}
      </DPSectionCard>

      {/* ── Reset ── */}
      <button type="button" onClick={() => {
        const keys = ['accentColor','nameColor','sectionColor','bodyColor','sidebarColor','linkColor','fontFamily','fontSizeMode','sectionCase','dateFormat','lineSpacing','pageSize','pageNumberVisible','addressFormat','margins','bulletSymbol','sectionDivider','skillsAsTags','showPhoto'];
        const reset = keys.reduce((a, k) => ({ ...a, [k]: undefined }), {});
        onChange({ ...data, personalInfo: { ...pi, ...reset } });
      }} style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'white', color: '#6B7280', border: '1px solid #E5E7EB', borderRadius: 8, padding: '9px 16px', fontSize: 12, fontWeight: 600, cursor: 'pointer', marginTop: 4, fontFamily: 'Inter, sans-serif' }}
        onMouseEnter={e => { e.currentTarget.style.borderColor = '#EF1D28'; e.currentTarget.style.color = '#EF1D28'; }}
        onMouseLeave={e => { e.currentTarget.style.borderColor = '#E5E7EB'; e.currentTarget.style.color = '#6B7280'; }}>
        <RotateCcw size={13} /> Reset to defaults
      </button>
    </div>
  );
};

const TEMPLATE_CATEGORIES = {
  modern:          'Professional',
  classic:         'Professional',
  executive:       'Executive',
  minimal:         'Minimal',
  creative:        'Creative',
  tech:            'Tech',
  elegant:         'Elegant',
  corporate:       'Professional',
  blue_corporate:  'Professional',
  navy_timeline:   'Professional',
  crimson:         'Professional',
  harvard_standard:'Professional',
  blue_analyst:    'Professional',
  russian_classic: 'Professional',
  gregory_sidebar: 'Professional',
  slate_sidebar:   'Professional',
  mono_pro:        'Minimal',
  ivory_serif:     'Elegant',
  teal_banner:     'Creative',
  swiss_grid:      'Minimal',
  amber_executive: 'Executive',
  violet_sidebar:  'Creative',
  bold_stripe:     'Professional',
  rose_gold:       'Elegant',
  forest_pro:      'Minimal',
  sky_pro:         'Professional',
  charcoal_split:  'Professional',
  terracotta:      'Elegant',
  startup_clean:   'Tech',
  champagne:       'Elegant',
  med_pro_blue:    'Medical',
  clinical_teal:   'Medical',
  nurse_rx:        'Medical',
  healthcare_pro:  'Medical',
  critical_care:   'Medical',
  realty_pro:      'Real Estate',
  estate_sidebar:  'Real Estate',
  luxury_broker:   'Real Estate',
  re_corporate:    'Real Estate',
  property_exec:   'Real Estate',
  shield_pro:      'Security',
  patrol_blue:     'Security',
  sentinel_dark:   'Security',
  guardian_grid:   'Security',
  tactical_clean:  'Security',
  exec_suite:      'Business',
  boardroom:       'Business',
  bizdev_pro:      'Business',
  strategy_blue:   'Business',
  c_suite:         'Business',
  bloom_sidebar:   'Beauty & Wellness',
  salon_chic:      'Beauty & Wellness',
  spa_zen:         'Beauty & Wellness',
  glow_pro:        'Beauty & Wellness',
  beauty_badge:    'Beauty & Wellness',
};

const TemplatePicker = ({ current, onChange, data, type }) => {
  const [filter, setFilter] = useState('All');
  const [hovered, setHovered] = useState(null);
  const isCL = type === 'cover_letter';
  const isCV = type === 'cv';
  const previewBase = data || (isCL ? SAMPLE_CL : SAMPLE_RESUME);

  const categories = (isCL || isCV) ? ['All'] : ['All', ...Array.from(new Set(Object.values(TEMPLATE_CATEGORIES)))];
  const visible = isCL
    ? CL_TEMPLATES
    : isCV
      ? CV_TEMPLATES
      : TEMPLATES.filter(t => filter === 'All' || TEMPLATE_CATEGORIES[t.id] === filter);

  const cleanPi = (pi) => ({
    ...pi,
    fontFamily: undefined, accentColor: undefined, sectionColor: undefined,
    bodyColor: undefined, sidebarColor: undefined, linkColor: undefined, nameColor: undefined,
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* ── Sticky header ── */}
      <div style={{ padding: '16px 16px 0', background: '#F9FAFB', borderBottom: '1px solid #F3F4F6', position: 'sticky', top: 0, zIndex: 10 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 10 }}>
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#111827' }}>Choose Template</h3>
          <span style={{ fontSize: 11, color: '#6B7280' }}>{visible.length} layouts</span>
        </div>
        {/* Filter chips */}
        <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', paddingBottom: 12, overflowX: 'auto' }}>
          {categories.map(cat => {
            const active = filter === cat;
            return (
              <button key={cat} type="button" onClick={() => setFilter(cat)} style={{
                padding: '4px 11px', border: `1.5px solid ${active ? '#111827' : '#E5E7EB'}`,
                borderRadius: 999, background: active ? '#111827' : 'white',
                color: active ? 'white' : '#374151', fontSize: 11, fontWeight: active ? 600 : 400,
                cursor: 'pointer', whiteSpace: 'nowrap', fontFamily: 'Inter, sans-serif',
                transition: 'all 0.12s',
              }}>{cat}</button>
            );
          })}
        </div>
      </div>

      {/* ── Grid ── */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '14px 16px 24px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12 }}>
          {visible.map(tmpl => {
            const active = current === tmpl.id;
            const isHov = hovered === tmpl.id;
            return (
              <div key={tmpl.id}
                onMouseEnter={() => setHovered(tmpl.id)}
                onMouseLeave={() => setHovered(null)}
                style={{ position: 'relative', borderRadius: 12, overflow: 'hidden', cursor: 'pointer',
                  border: `2px solid ${active ? tmpl.color : isHov ? '#D1D5DB' : '#E5E7EB'}`,
                  boxShadow: active ? `0 0 0 3px ${tmpl.color}28, 0 4px 16px rgba(0,0,0,0.08)` : isHov ? '0 4px 16px rgba(0,0,0,0.10)' : '0 1px 4px rgba(0,0,0,0.05)',
                  transform: isHov && !active ? 'translateY(-2px)' : 'none',
                  transition: 'all 0.18s',
                }}>

                {/* Preview area */}
                <div style={{ height: 200, overflow: 'hidden', background: '#F8FAFC', position: 'relative' }}
                  onClick={() => onChange(tmpl.id)}>
                  <div style={{ width: 800, position: 'absolute', top: 0, left: '50%', marginLeft: -400, transform: 'scale(0.345)', transformOrigin: 'top center', pointerEvents: 'none', userSelect: 'none' }}>
                    <ResumePreview id={`tmpl-thumb-${tmpl.id}`} data={{ ...previewBase, template: tmpl.id, personalInfo: cleanPi({ ...(previewBase.personalInfo || {}), ...(PHOTO_TEMPLATES.has(tmpl.id) ? {} : { photo: undefined, showPhoto: false }) }) }} singlePage={true} />
                  </div>

                  {/* Hover overlay */}
                  {isHov && !active && (
                    <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.32)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <div style={{ background: 'white', color: '#111827', borderRadius: 8, padding: '7px 16px', fontSize: 12, fontWeight: 600, fontFamily: 'Inter, sans-serif', boxShadow: '0 2px 8px rgba(0,0,0,0.2)' }}>
                        Apply Template
                      </div>
                    </div>
                  )}

                  {/* Active badge */}
                  {active && (
                    <div style={{ position: 'absolute', top: 8, right: 8, background: tmpl.color, color: 'white', borderRadius: '50%', width: 22, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2, boxShadow: '0 2px 6px rgba(0,0,0,0.25)' }}>
                      <Check size={12} strokeWidth={3} />
                    </div>
                  )}

                  {/* Category chip */}
                  {!isCL && (
                    <div style={{ position: 'absolute', top: 8, left: 8, background: 'rgba(255,255,255,0.92)', borderRadius: 5, padding: '2px 7px', fontSize: 9, fontWeight: 600, color: '#374151', backdropFilter: 'blur(4px)' }}>
                      {TEMPLATE_CATEGORIES[tmpl.id]}
                    </div>
                  )}
                </div>

                {/* Label row */}
                <div style={{ padding: '9px 10px 10px', background: active ? tmpl.color + '08' : 'white', borderTop: `1px solid ${active ? tmpl.color + '33' : '#F3F4F6'}` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
                    <span style={{ fontWeight: 700, color: active ? tmpl.color : '#111827', fontSize: 12 }}>{tmpl.name}</span>
                    <span style={{ fontSize: 9, background: '#D1FAE5', color: '#065F46', padding: '1px 6px', borderRadius: 10, fontWeight: 700 }}>FREE</span>
                  </div>
                  {tmpl.desc && <div style={{ fontSize: 10, color: '#9CA3AF', lineHeight: 1.3 }}>{tmpl.desc}</div>}
                </div>
              </div>
            );
          })}
        </div>

        {visible.length === 0 && (
          <div style={{ textAlign: 'center', padding: '40px 20px', color: '#9CA3AF' }}>
            <div style={{ fontSize: 13 }}>No templates in this category</div>
          </div>
        )}
      </div>
    </div>
  );
};

// ── AI Generate Modal ─────────────────────────────────────────────────────────
const AIModal = ({ type, onGenerate, onClose, loading, initialInput = '', initialJobDesc = '' }) => {
  const [input, setInput]   = useState(initialInput);
  const [jobDesc, setJobDesc] = useState(initialJobDesc);
  const ta = { width: '100%', padding: '10px 12px', border: '1.5px solid #E5E7EB', borderRadius: 8, fontSize: 13, resize: 'vertical', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' };
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ background: 'white', borderRadius: 16, padding: 28, width: '100%', maxWidth: 560, boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 18 }}>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800 }}>✨ Generate with AI</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#6B7280' }}>×</button>
        </div>
        <div style={{ marginBottom: 14 }}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6, color: '#374151' }}>
            {type === 'cover_letter' ? 'Describe yourself + target company & role' : 'Describe your background, experience & target role'}
          </label>
          <textarea style={{ ...ta, height: 130 }} value={input} onChange={e => setInput(e.target.value)}
            placeholder="e.g. My name is Ahmed Khan, 5 years experience as software engineer. Skills in React, Node.js, Python, AWS. CS degree from NUST. Looking for senior roles..." />
        </div>
        {type !== 'cover_letter' && (
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6, color: '#374151' }}>Job Description (optional — for ATS optimization)</label>
            <textarea style={{ ...ta, height: 80 }} value={jobDesc} onChange={e => setJobDesc(e.target.value)} placeholder="Paste the job description or job URL here..." />
          </div>
        )}
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={() => onGenerate(input, jobDesc)} disabled={loading || !input.trim()}
            style={{ flex: 1, background: loading ? '#C4B5FD' : 'linear-gradient(135deg,#FB923C,#F97316)', color: 'white', border: 'none', borderRadius: 8, padding: '11px', fontWeight: 700, fontSize: 14, cursor: loading || !input.trim() ? 'not-allowed' : 'pointer' }}>
            {loading ? '⏳ Generating...' : '✨ Generate Now'}
          </button>
          <button onClick={onClose} style={{ padding: '11px 20px', background: '#F3F4F6', color: '#374151', border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
        </div>
      </div>
    </div>
  );
};

// ── ATS Panel ────────────────────────────────────────────────────────────────

const ATSGradientBar = ({ score, height = 12 }) => {
  const pct = Math.min(Math.max(score, 0), 100);
  const color = score >= 80 ? '#22C55E' : score >= 60 ? '#F97316' : '#EF4444';
  return (
    <div style={{ position: 'relative', width: '100%', height, borderRadius: height / 2, overflow: 'visible', background: 'linear-gradient(to right,#EF4444 0%,#F97316 30%,#EAB308 60%,#22C55E 100%)' }}>
      <div style={{
        position: 'absolute', top: '50%', left: `${pct}%`,
        transform: 'translate(-50%,-50%)',
        width: height + 4, height: height + 8,
        background: '#111827', borderRadius: 3,
        boxShadow: `0 0 0 2px white, 0 0 0 4px ${color}`,
        zIndex: 2,
      }} />
    </div>
  );
};

const ScoreRing = ({ score, size = 80 }) => {
  const r = (size - 10) / 2;
  const circ = 2 * Math.PI * r;
  const fill = (score / 100) * circ;
  const color = score >= 80 ? '#22C55E' : score >= 60 ? '#F97316' : '#EF4444';
  return (
    <svg width={size} height={size} style={{ transform: 'rotate(-90deg)', flexShrink: 0 }}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#F3F4F6" strokeWidth={8} />
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={8}
        strokeDasharray={`${fill} ${circ}`} strokeLinecap="round" style={{ transition: 'stroke-dasharray 1s ease' }} />
    </svg>
  );
};

const ATS_STEPS = [
  'Reading your resume…',
  'Checking contact info…',
  'Scoring keywords & content…',
  'Evaluating structure…',
  'Checking ATS formatting…',
  'Calculating your score…',
  'Generating suggestions…',
];

const atsStr = (v) => {
  if (typeof v === 'string') return v;
  if (v && typeof v === 'object') return v.label || v.text || v.description || v.requirement || Object.values(v).find(x => typeof x === 'string') || '';
  return String(v ?? '');
};

const ATSPanel = ({ data, onAnalyze, loading, onImprove, sectionLoading, onUpdate }) => {
  const [report, setReport]             = useState(null);
  const [expanded, setExpanded]         = useState(null);
  const [view, setView]                 = useState('summary');
  const [detailCat, setDetailCat]       = useState(null);
  const [stepIdx, setStepIdx]           = useState(0);
  const [pendingFix, setPendingFix]             = useState(null);
  const [autoReanalyzing, setAutoReanalyzing]   = useState(false);
  const [expandedImprove, setExpandedImprove]   = useState(null);
  const [atsSettingsApplied, setAtsSettingsApplied] = useState(false);
  const stepTimer = React.useRef(null);

  React.useEffect(() => {
    if (loading) {
      setStepIdx(0);
      stepTimer.current = setInterval(() => {
        setStepIdx(i => Math.min(i + 1, ATS_STEPS.length - 1));
      }, 1800);
    } else {
      clearInterval(stepTimer.current);
    }
    return () => clearInterval(stepTimer.current);
  }, [loading]);

  const handleAnalyze = async () => {
    try {
      const result = await onAnalyze(data);
      setReport(result);
      setView('summary');
    } catch (_) {}
  };

  const getSectionIssues = () => {
    const issues = [];
    const contentCat = report?.categories?.find(c => c.name === 'Content');
    contentCat?.issues?.forEach(i => issues.push(atsStr(i)));
    report?.suggestions?.filter(s => s.category === 'Content').forEach(s => issues.push(atsStr(s.text)));
    return [...new Set(issues)].slice(0, 6);
  };

  const handleImprove = async (sectionKey) => {
    try {
      const improved = await onImprove(data, sectionKey, getSectionIssues());
      setPendingFix({ section: sectionKey, improved });
      setView('fix');
    } catch (_) {}
  };

  const handleApplyFix = async () => {
    if (!pendingFix) return;
    const { section, improved } = pendingFix;
    let updatedData = { ...data };
    if (section === 'summary') {
      updatedData = { ...data, personalInfo: { ...data.personalInfo, summary: improved } };
    } else if (section === 'skills') {
      updatedData = { ...data, skills: improved };
    } else if (section === 'experience') {
      updatedData = {
        ...data,
        experience: data.experience.map(exp => {
          const upd = Array.isArray(improved) ? improved.find(u => u.id === exp.id) : null;
          return upd ? { ...exp, bullets: upd.bullets } : exp;
        }),
      };
    }
    onUpdate(updatedData);
    setPendingFix(null);
    setView('summary');
    setAutoReanalyzing(true);
    try {
      const result = await onAnalyze(updatedData);
      if (result) setReport(result);
    } catch (_) {}
    setAutoReanalyzing(false);
  };

  const priorityConfig = {
    high:   { label: 'High',   bg: '#FFF1F2', color: '#EF1D28', border: '#FECACA' },
    medium: { label: 'Medium', bg: '#FFF7ED', color: '#F97316', border: '#FED7AA' },
    low:    { label: 'Low',    bg: '#F0FDF4', color: '#16A34A', border: '#BBF7D0' },
  };

  const scoreColor = (s) => s >= 80 ? '#16A34A' : s >= 60 ? '#EA580C' : '#DC2626';
  const scoreBg = (s) => s >= 80 ? '#ECFDF5' : s >= 60 ? '#FFF7ED' : '#FFF1F2';

  /* ── AI Fix preview view ── */
  if (view === 'fix' && pendingFix) {
    const sectionLabels = { summary: 'Summary', skills: 'Skills', experience: 'Experience Bullets' };
    const label = sectionLabels[pendingFix.section] || pendingFix.section;
    return (
      <div style={{ padding: '20px 18px', height: '100%', overflowY: 'auto' }}>
        <button type="button" onClick={() => { setPendingFix(null); setView('summary'); }}
          style={{ display: 'flex', alignItems: 'center', gap: 6, border: 'none', background: 'transparent', cursor: 'pointer', color: '#6B7280', fontSize: 13, fontWeight: 500, padding: '0 0 16px', marginBottom: 4 }}>
          <ArrowLeft size={15} /> Back
        </button>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
          <div style={{ width: 40, height: 40, borderRadius: 12, background: 'linear-gradient(135deg,#7C3AED,#A78BFA)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Sparkles size={18} color="white" />
          </div>
          <div>
            <div style={{ fontSize: 15, fontWeight: 800, color: '#111827' }}>AI-Improved {label}</div>
            <div style={{ fontSize: 12, color: '#6B7280', marginTop: 1 }}>Review then apply to update your resume</div>
          </div>
        </div>

        <div style={{ background: '#F5F3FF', border: '1px solid #DDD6FE', borderRadius: 12, padding: '14px 16px', marginBottom: 16 }}>
          {pendingFix.section === 'summary' && (
            <p style={{ margin: 0, fontSize: 13, color: '#374151', lineHeight: 1.65 }}>{pendingFix.improved}</p>
          )}

          {pendingFix.section === 'skills' && Array.isArray(pendingFix.improved) && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {pendingFix.improved.map((sk, i) => (
                <div key={i}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#7C3AED', marginBottom: 3 }}>{sk.category}</div>
                  <div style={{ fontSize: 13, color: '#374151' }}>{sk.items}</div>
                </div>
              ))}
            </div>
          )}

          {pendingFix.section === 'experience' && Array.isArray(pendingFix.improved) && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {pendingFix.improved.map((exp, i) => {
                const orig = data.experience?.find(e => e.id === exp.id);
                return (
                  <div key={i}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#7C3AED', marginBottom: 6 }}>
                      {orig?.title}{orig?.company ? ` · ${orig.company}` : ''}
                    </div>
                    {(exp.bullets || []).map((b, j) => (
                      <div key={j} style={{ display: 'flex', gap: 7, marginBottom: 5 }}>
                        <span style={{ color: '#7C3AED', flexShrink: 0, marginTop: 1 }}>•</span>
                        <span style={{ fontSize: 13, color: '#374151', lineHeight: 1.55 }}>{b}</span>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" onClick={handleApplyFix} disabled={autoReanalyzing}
            style={{ flex: 1, background: autoReanalyzing ? '#86EFAC' : '#16A34A', color: 'white', border: 'none', borderRadius: 10, padding: '11px', fontWeight: 700, fontSize: 13, cursor: autoReanalyzing ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
            {autoReanalyzing
              ? <><Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} /> Updating score...</>
              : <><CheckCircle2 size={14} /> Apply & Re-analyze</>}
          </button>
          <button type="button" onClick={() => { setPendingFix(null); setView('summary'); }}
            style={{ padding: '11px 14px', background: 'white', color: '#374151', border: '1px solid #E5E7EB', borderRadius: 10, fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
            Dismiss
          </button>
        </div>
      </div>
    );
  }

  /* ── Category Detail view ── */
  if (view === 'detail' && detailCat) {
    return (
      <div style={{ padding: '20px 18px', height: '100%', overflowY: 'auto' }}>
        <button type="button" onClick={() => setView('summary')}
          style={{ display: 'flex', alignItems: 'center', gap: 6, border: 'none', background: 'transparent', cursor: 'pointer', color: '#6B7280', fontSize: 13, fontWeight: 500, padding: '0 0 16px', marginBottom: 4 }}>
          <ArrowLeft size={15} /> Back
        </button>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18 }}>
          <div style={{ width: 44, height: 44, borderRadius: 12, background: scoreBg(detailCat.score), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            {detailCat.name === 'Design' ? <Palette size={20} color={scoreColor(detailCat.score)} /> :
             detailCat.name === 'Structure' ? <Layout size={20} color={scoreColor(detailCat.score)} /> :
             <FileText size={20} color={scoreColor(detailCat.score)} />}
          </div>
          <div>
            <div style={{ fontSize: 16, fontWeight: 800, color: '#111827' }}>{detailCat.name}</div>
            <div style={{ fontSize: 12, color: '#6B7280' }}>{detailCat.description}</div>
          </div>
          <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
            <div style={{ fontSize: 22, fontWeight: 800, color: scoreColor(detailCat.score) }}>{detailCat.score}</div>
            <div style={{ fontSize: 11, color: '#9CA3AF' }}>/ 100</div>
          </div>
        </div>

        <ATSGradientBar score={detailCat.score} height={10} />
        <div style={{ height: 1, background: '#F3F4F6', margin: '20px 0' }} />

        {detailCat.issues?.length > 0 && (
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#EF1D28', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>
              Issues to fix
            </div>
            {detailCat.issues.map((issue, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 9, marginBottom: 9 }}>
                <div style={{ width: 20, height: 20, borderRadius: 6, background: '#FFF1F2', border: '1px solid #FECACA', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>
                  <AlertTriangle size={11} color="#EF1D28" />
                </div>
                <span style={{ fontSize: 13, color: '#374151', lineHeight: 1.55 }}>{atsStr(issue)}</span>
              </div>
            ))}
          </div>
        )}

        {detailCat.passed?.length > 0 && (
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#16A34A', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>
              What's working
            </div>
            {detailCat.passed.map((p, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 9, marginBottom: 9 }}>
                <div style={{ width: 20, height: 20, borderRadius: 6, background: '#ECFDF5', border: '1px solid #BBF7D0', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>
                  <CheckCircle2 size={11} color="#16A34A" />
                </div>
                <span style={{ fontSize: 13, color: '#374151', lineHeight: 1.55 }}>{atsStr(p)}</span>
              </div>
            ))}
          </div>
        )}

        {/* Fix with AI — Content & Structure */}
        {detailCat.score < 90 && detailCat.name !== 'Design' && (
          <div style={{ borderTop: '1px solid #F3F4F6', paddingTop: 18 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#7C3AED', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 5 }}>
              <Sparkles size={12} /> Fix with AI
            </div>
            {detailCat.name === 'Content' ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                {[
                  { key: 'summary',    label: 'Improve Summary',            has: !!data?.personalInfo?.summary },
                  { key: 'skills',     label: 'Improve Skills',             has: !!(data?.skills?.length) },
                  { key: 'experience', label: 'Improve Experience Bullets', has: !!(data?.experience?.length) },
                ].filter(s => s.has).map(({ key, label }) => {
                  const isImproving = sectionLoading === key;
                  return (
                    <button key={key} type="button" onClick={() => handleImprove(key)} disabled={!!sectionLoading}
                      style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', background: isImproving ? '#F5F3FF' : 'white', border: `1px solid ${isImproving ? '#A78BFA' : '#E5E7EB'}`, borderRadius: 10, cursor: sectionLoading ? 'not-allowed' : 'pointer', color: '#374151', fontSize: 13, fontWeight: 500, textAlign: 'left' }}>
                      {isImproving
                        ? <Loader2 size={14} color="#7C3AED" style={{ animation: 'spin 1s linear infinite', flexShrink: 0 }} />
                        : <Sparkles size={14} color="#7C3AED" style={{ flexShrink: 0 }} />}
                      <span style={{ flex: 1 }}>{isImproving ? `${label.replace('Improve ', 'Improving ')}…` : label}</span>
                      {!isImproving && <ChevronRight size={13} color="#9CA3AF" />}
                    </button>
                  );
                })}
              </div>
            ) : (
              <button type="button" onClick={() => handleImprove('experience')} disabled={!!sectionLoading}
                style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', background: sectionLoading === 'experience' ? '#F5F3FF' : 'white', border: `1px solid ${sectionLoading === 'experience' ? '#A78BFA' : '#E5E7EB'}`, borderRadius: 10, cursor: sectionLoading ? 'not-allowed' : 'pointer', color: '#374151', fontSize: 13, fontWeight: 500 }}>
                {sectionLoading === 'experience'
                  ? <Loader2 size={14} color="#7C3AED" style={{ animation: 'spin 1s linear infinite', flexShrink: 0 }} />
                  : <Sparkles size={14} color="#7C3AED" style={{ flexShrink: 0 }} />}
                <span style={{ flex: 1 }}>{sectionLoading === 'experience' ? 'Improving Structure…' : 'Fix Structure with AI'}</span>
                {!sectionLoading && <ChevronRight size={13} color="#9CA3AF" />}
              </button>
            )}
          </div>
        )}

        {/* Fix Design — ATS-friendly templates + Quick Settings */}
        {detailCat.score < 90 && detailCat.name === 'Design' && (() => {
          const atsFriendly = TEMPLATES.filter(t => /ATS/i.test(t.desc));
          const handleApplyATSSettings = () => {
            const pi = data?.personalInfo || {};
            onUpdate({
              ...data,
              personalInfo: {
                ...pi,
                fontFamily: 'Inter',
                fontSizeMode: 'normal',
                margins: 'normal',
                sectionCase: 'uppercase',
                ...(DIVIDER_TEMPLATES.has(data?.template) ? { sectionDivider: 'line' } : {}),
              },
            });
            setAtsSettingsApplied(true);
          };
          return (
            <div style={{ borderTop: '1px solid #F3F4F6', paddingTop: 18 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#16A34A', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 5 }}>
                <Palette size={12} /> Fix Design
              </div>

              {/* ATS-friendly template thumbnails */}
              <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 8 }}>ATS-Friendly Templates</div>
              <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 8, scrollbarWidth: 'none' }}>
                {atsFriendly.map(tmpl => {
                  const isActive = data?.template === tmpl.id;
                  return (
                    <button key={tmpl.id} type="button"
                      onClick={() => { onUpdate({ ...data, template: tmpl.id }); setAtsSettingsApplied(true); }}
                      title={tmpl.name}
                      style={{ flexShrink: 0, width: 88, background: 'none', border: `2px solid ${isActive ? '#16A34A' : '#E5E7EB'}`, borderRadius: 10, overflow: 'hidden', cursor: 'pointer', padding: 0, position: 'relative' }}>
                      <div style={{ height: 110, overflow: 'hidden', position: 'relative', background: '#F8FAFC' }}>
                        <div style={{ width: 794, position: 'absolute', top: 0, left: 0, transform: 'scale(0.111)', transformOrigin: 'top left', pointerEvents: 'none' }}>
                          <ResumePreview id={`ats-tmpl-${tmpl.id}`} data={{ ...data, template: tmpl.id }} singlePage />
                        </div>
                      </div>
                      <div style={{ padding: '5px 6px', borderTop: '1px solid #F3F4F6', background: 'white' }}>
                        <div style={{ fontSize: 10, fontWeight: 600, color: isActive ? '#16A34A' : '#374151', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{tmpl.name}</div>
                        {isActive && <div style={{ fontSize: 9, color: '#16A34A', fontWeight: 600 }}>Current</div>}
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Quick settings fix */}
              <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginTop: 14, marginBottom: 8 }}>Quick Settings Fix</div>
              <button type="button" onClick={handleApplyATSSettings}
                style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '11px 14px', background: 'white', border: '1px solid #E5E7EB', borderRadius: 10, cursor: 'pointer', color: '#374151', fontSize: 13, fontWeight: 500, textAlign: 'left' }}>
                <Zap size={14} color="#F97316" style={{ flexShrink: 0 }} />
                <div style={{ flex: 1 }}>
                  <div>Apply ATS Settings</div>
                  <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 2 }}>Font: Inter · Margins: Normal · Headings: UPPERCASE</div>
                </div>
                <ChevronRight size={13} color="#9CA3AF" />
              </button>

              {atsSettingsApplied && (
                <button type="button" onClick={async () => { setAtsSettingsApplied(false); setAutoReanalyzing(true); try { const r = await onAnalyze(data); if (r) setReport(r); } catch (_) {} setAutoReanalyzing(false); setView('summary'); }}
                  style={{ marginTop: 10, width: '100%', textAlign: 'center', fontSize: 12, color: '#16A34A', fontWeight: 600, background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 8, padding: '8px', cursor: 'pointer' }}>
                  ✓ Changes applied — Re-analyze to update score
                </button>
              )}
            </div>
          );
        })()}
      </div>
    );
  }

  /* ── No report yet ── */
  if (!report) {
    return (
      <div style={{ padding: '20px 18px', height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
          <div style={{ width: 40, height: 40, borderRadius: 12, background: 'linear-gradient(135deg,#EF1D28,#FB923C)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <BarChart2 size={20} color="white" strokeWidth={2.2} />
          </div>
          <div>
            <div style={{ fontSize: 15, fontWeight: 800, color: '#111827' }}>ATS Score</div>
            <div style={{ fontSize: 12, color: '#6B7280', marginTop: 1 }}>Check your resume's ATS compatibility.</div>
          </div>
        </div>

        <div style={{ height: 1, background: '#F3F4F6', margin: '16px 0' }} />

        {/* Empty state / loading state */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: '20px 10px' }}>
          {loading ? (
            <RocketLoader size={160} message={ATS_STEPS[stepIdx]} />
          ) : (
            /* ── Default idle state ── */
            <>
              <div style={{ width: 80, height: 80, borderRadius: 20, background: 'linear-gradient(135deg,#FFF1E8,#FFE7D6)', border: '1.5px solid #FED7AA', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 20 }}>
                <BarChart2 size={38} color="#F97316" strokeWidth={1.5} />
              </div>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#111827', marginBottom: 8 }}>Check ATS Compatibility</div>
              <p style={{ margin: '0 0 24px', fontSize: 13, color: '#6B7280', lineHeight: 1.65, maxWidth: 240 }}>
                Get a score out of 100 for Content, Structure, and Design — plus actionable tips to improve.
              </p>
              <button type="button" onClick={handleAnalyze}
                style={{ border: 'none', borderRadius: 10, padding: '12px 28px', fontSize: 14, fontWeight: 700, cursor: 'pointer', background: 'linear-gradient(135deg,#EF1D28,#FB923C)', color: 'white', display: 'flex', alignItems: 'center', gap: 8, margin: '0 auto' }}>
                <Zap size={15} /> Analyze My Resume
              </button>
              <div style={{ marginTop: 28, display: 'flex', flexDirection: 'column', gap: 10, width: '100%' }}>
                {[
                  { Icon: FileText,  label: 'Content',   desc: 'Keywords, bullets & impact' },
                  { Icon: Layout,    label: 'Structure',  desc: 'Sections, order & completeness' },
                  { Icon: Palette,   label: 'Design',     desc: 'Template, layout & formatting' },
                ].map(({ Icon, label, desc }) => (
                  <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', background: '#F9FAFB', borderRadius: 9, border: '1px solid #F3F4F6' }}>
                    <Icon size={14} color="#9CA3AF" />
                    <div>
                      <span style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>{label}</span>
                      <span style={{ fontSize: 11, color: '#9CA3AF', marginLeft: 6 }}>{desc}</span>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  /* ── Report summary ── */
  return (
    <div style={{ padding: '20px 18px', height: '100%', overflowY: 'auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 36, height: 36, borderRadius: 10, background: 'linear-gradient(135deg,#EF1D28,#FB923C)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <BarChart2 size={18} color="white" />
          </div>
          <div style={{ fontSize: 15, fontWeight: 800, color: '#111827' }}>ATS Metrics</div>
        </div>
        <button type="button" onClick={handleAnalyze} disabled={loading} title="Re-analyze"
          style={{ border: '1px solid #E5E7EB', background: 'white', borderRadius: 8, padding: '6px 10px', cursor: loading ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 5, color: loading ? '#EF1D28' : '#6B7280', fontSize: 12, fontWeight: 500, minWidth: 100, justifyContent: 'center' }}>
          <RefreshCw size={13} style={loading ? { animation: 'spin 1s linear infinite' } : {}} />
          {loading ? ATS_STEPS[stepIdx] : 'Re-analyze'}
        </button>
      </div>

      {/* Score card */}
      <div style={{ background: 'white', border: '1px solid #E5E7EB', borderRadius: 14, padding: '18px 20px', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16 }}>
          <div style={{ position: 'relative', flexShrink: 0 }}>
            <ScoreRing score={report.overall} size={72} />
            <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
              {autoReanalyzing
                ? <Loader2 size={18} color="#7C3AED" style={{ animation: 'spin 1s linear infinite' }} />
                : <><span style={{ fontSize: 17, fontWeight: 800, color: '#111827', lineHeight: 1 }}>{report.overall}</span><span style={{ fontSize: 9, color: '#9CA3AF', fontWeight: 600 }}>/100</span></>}
            </div>
          </div>
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: 0.5 }}>ATS Score</div>
            <div style={{ fontSize: 24, fontWeight: 800, color: '#111827', lineHeight: 1.1 }}>
              {autoReanalyzing ? <span style={{ fontSize: 13, color: '#7C3AED', fontWeight: 600 }}>Recalculating…</span> : `${report.overall}/100`}
            </div>
            <div style={{ fontSize: 12, color: '#6B7280', marginTop: 4 }}>
              Weakest: <strong style={{ color: '#EF1D28' }}>{report.weakestLink}</strong>
            </div>
          </div>
        </div>
        <ATSGradientBar score={report.overall} height={12} />
      </div>

      {/* Category breakdown */}
      <div style={{ background: 'white', border: '1px solid #E5E7EB', borderRadius: 14, overflow: 'hidden', marginBottom: 16 }}>
        {report.categories?.map((cat, i) => (
          <button key={cat.name} type="button"
            onClick={() => { setDetailCat(cat); setView('detail'); }}
            style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '13px 16px', border: 'none', borderBottom: i < report.categories.length - 1 ? '1px solid #F3F4F6' : 'none', background: 'white', cursor: 'pointer', gap: 12, textAlign: 'left' }}
            onMouseEnter={e => e.currentTarget.style.background = '#FAFAFA'}
            onMouseLeave={e => e.currentTarget.style.background = 'white'}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1 }}>
              <div style={{ width: 32, height: 32, borderRadius: 8, background: scoreBg(cat.score), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                {cat.name === 'Design' ? <Palette size={15} color={scoreColor(cat.score)} /> :
                 cat.name === 'Structure' ? <Layout size={15} color={scoreColor(cat.score)} /> :
                 <FileText size={15} color={scoreColor(cat.score)} />}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 5 }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>{cat.name}</span>
                  <span style={{ fontSize: 11, color: '#9CA3AF' }}>
                    {{ Content: 'Keywords, bullets & impact', Structure: 'Sections & formatting', Design: 'Template & layout' }[cat.name]}
                  </span>
                </div>
                <div style={{ marginTop: 4, height: 4, background: '#F3F4F6', borderRadius: 2, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${cat.score}%`, background: cat.score >= 80 ? '#22C55E' : cat.score >= 60 ? '#F97316' : '#EF4444', borderRadius: 2, transition: 'width 0.6s ease' }} />
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: scoreColor(cat.score) }}>{cat.score}/100</span>
              <ChevronRight size={14} color="#9CA3AF" />
            </div>
          </button>
        ))}
      </div>

      {/* Suggestions */}
      {report.suggestions?.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#111827', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
            <TrendingUp size={14} color="#F97316" /> Suggestions to Improve
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {report.suggestions.map((s, i) => {
              const cfg = priorityConfig[s.priority] || priorityConfig.medium;
              return (
                <div key={i} style={{ background: 'white', border: '1px solid #E5E7EB', borderRadius: 10, padding: '11px 13px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 5 }}>
                    <span style={{ fontSize: 10, fontWeight: 700, color: cfg.color, background: cfg.bg, border: `1px solid ${cfg.border}`, borderRadius: 5, padding: '2px 7px', letterSpacing: 0.3, textTransform: 'uppercase' }}>{cfg.label}</span>
                    <span style={{ fontSize: 11, color: '#9CA3AF' }}>{s.category}</span>
                  </div>
                  <div style={{ fontSize: 13, color: '#374151', lineHeight: 1.55 }}>{atsStr(s.text)}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Strengths */}
      {report.strengths?.length > 0 && (
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#111827', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
            <ShieldCheck size={14} color="#16A34A" /> Your Strengths
          </div>
          <div style={{ background: 'white', border: '1px solid #E5E7EB', borderRadius: 10, overflow: 'hidden' }}>
            {report.strengths.map((s, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 9, padding: '10px 14px', borderBottom: i < report.strengths.length - 1 ? '1px solid #F9FAFB' : 'none' }}>
                <CheckCircle2 size={14} color="#16A34A" strokeWidth={2.5} style={{ flexShrink: 0, marginTop: 1 }} />
                <span style={{ fontSize: 13, color: '#374151', lineHeight: 1.5 }}>{atsStr(s)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ── Tailor Modal ──────────────────────────────────────────────────────────────
const TailorPanel = ({ onTailor, loading, type }) => {
  const [mode, setMode]       = useState('manual');
  const [jd, setJd]           = useState('');
  const [url, setUrl]         = useState('');
  const [tailored, setTailored] = useState(false);
  const { showAlert }         = useDialog();
  const isCL = type === 'cover_letter';

  const canSubmit = mode === 'manual' ? jd.trim().length > 0 : url.trim().length > 0;

  const handleSubmit = async () => {
    if (mode === 'url') {
      await showAlert('Job URL auto-fetch is coming soon! Please switch to "Paste Manually" and paste the job description.', {
        type: 'info', title: 'Coming Soon',
      });
      setMode('manual');
      return;
    }
    try {
      await onTailor(jd);
      setTailored(true);
    } catch (_) {
      setTailored(false);
    }
  };

  const tips = isCL
    ? [
        'Paste the full job description including role requirements.',
        'AI rewrites your opening, body, and closing to match the role.',
        'Tailored cover letters significantly increase interview callback rates.',
      ]
    : [
        'Paste the complete job post including requirements and responsibilities.',
        'AI matches your existing skills to the job keywords automatically.',
        'Tailored resumes score 40%+ higher on ATS systems on average.',
      ];

  return (
    <div style={{ padding: '20px 18px', height: '100%', overflowY: 'auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
        <div style={{ width: 40, height: 40, borderRadius: 12, background: 'linear-gradient(135deg,#EF1D28,#FB923C)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Target size={20} color="white" strokeWidth={2.2} />
        </div>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 15, fontWeight: 800, color: '#111827' }}>{isCL ? 'Cover Letter Tailoring' : 'Resume Tailoring'}</span>
            <span style={{ fontSize: 10, fontWeight: 800, color: 'white', background: 'linear-gradient(135deg,#FB923C,#F97316)', borderRadius: 5, padding: '2px 7px', letterSpacing: 0.5 }}>BETA</span>
          </div>
          <div style={{ fontSize: 12, color: '#6B7280', marginTop: 1 }}>{isCL ? 'AI rewrites your letter to match the role.' : 'AI rewrites your resume to match the role.'}</div>
        </div>
      </div>

      <div style={{ height: 1, background: '#F3F4F6', margin: '16px 0' }} />

      {/* Mode tabs */}
      <div style={{ display: 'flex', background: '#F3F4F6', borderRadius: 9, padding: 3, gap: 2, marginBottom: 16 }}>
        {[
          { id: 'manual', label: 'Paste Manually', Icon: ClipboardPaste },
          { id: 'url',    label: 'Job URL',         Icon: Link2 },
        ].map(({ id, label, Icon }) => (
          <button key={id} type="button" onClick={() => { setMode(id); setTailored(false); }}
            style={{
              flex: 1, border: 'none', borderRadius: 7, padding: '8px 0', fontSize: 12, fontWeight: 600,
              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
              background: mode === id ? 'white' : 'transparent',
              color: mode === id ? '#111827' : '#6B7280',
              boxShadow: mode === id ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
              transition: 'all 0.15s',
            }}>
            <Icon size={12} strokeWidth={2} /> {label}
          </button>
        ))}
      </div>

      {/* Description */}
      <p style={{ margin: '0 0 14px', fontSize: 13, color: '#6B7280', lineHeight: 1.65 }}>
        {mode === 'manual'
          ? (isCL ? 'Paste the full job description below. AI will rewrite your cover letter to match the role.' : 'Paste the full job description below. AI will align your resume with the role and boost ATS keywords.')
          : (isCL ? 'Enter the job posting URL. We\'ll tailor your cover letter automatically.' : 'Enter the job posting URL. We\'ll detect the requirements and tailor your resume automatically.')}
      </p>

      {/* Input area */}
      {mode === 'manual' ? (
        <div style={{ marginBottom: 4 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>Job description <span style={{ color: '#EF1D28' }}>*</span></label>
            <span style={{ fontSize: 11, color: jd.length > 5500 ? '#EF1D28' : '#9CA3AF' }}>{jd.length} / 6000</span>
          </div>
          <textarea
            value={jd}
            onChange={e => { if (e.target.value.length <= 6000) setJd(e.target.value); setTailored(false); }}
            placeholder="Paste the full job description, requirements, and responsibilities here…"
            style={{
              width: '100%', border: '1.5px solid #E5E7EB', borderRadius: 10,
              padding: '11px 13px', fontSize: 13, outline: 'none', resize: 'vertical',
              boxSizing: 'border-box', fontFamily: 'inherit', lineHeight: 1.6,
              color: '#374151', minHeight: 200,
            }}
            onFocus={e => { e.target.style.borderColor = '#EF1D28'; }}
            onBlur={e => { e.target.style.borderColor = '#E5E7EB'; }}
          />
        </div>
      ) : (
        <div style={{ marginBottom: 4 }}>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
            Job post URL <span style={{ color: '#EF1D28' }}>*</span>
          </label>
          <input
            value={url}
            onChange={e => { setUrl(e.target.value); setTailored(false); }}
            placeholder="https://careers.example.com/software-engineer-123"
            style={{
              width: '100%', border: '1.5px solid #E5E7EB', borderRadius: 10,
              padding: '11px 13px', fontSize: 13, outline: 'none',
              boxSizing: 'border-box', fontFamily: 'inherit', color: '#374151',
            }}
            onFocus={e => { e.target.style.borderColor = '#EF1D28'; }}
            onBlur={e => { e.target.style.borderColor = '#E5E7EB'; }}
          />
          <div style={{ marginTop: 10, padding: '10px 13px', background: '#FFF7ED', border: '1px solid #FED7AA', borderRadius: 9, display: 'flex', alignItems: 'flex-start', gap: 8 }}>
            <span style={{ fontSize: 14, flexShrink: 0 }}>⚡</span>
            <span style={{ fontSize: 12, color: '#92400E', lineHeight: 1.55 }}>URL auto-fetch is coming soon. Switch to <strong>Paste Manually</strong> to tailor now.</span>
          </div>
        </div>
      )}

      {/* Tailor button */}
      <button
        type="button"
        onClick={handleSubmit}
        disabled={loading || !canSubmit}
        style={{
          width: '100%', marginTop: 14, border: 'none', borderRadius: 10, padding: '13px',
          fontSize: 14, fontWeight: 700, cursor: loading || !canSubmit ? 'not-allowed' : 'pointer',
          background: loading ? '#F3F4F6' : !canSubmit ? '#F3F4F6' : 'linear-gradient(135deg,#EF1D28,#FB923C)',
          color: loading || !canSubmit ? '#9CA3AF' : 'white',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          transition: 'all 0.2s',
        }}
      >
        {loading
          ? <><Loader2 size={15} style={{ animation: 'spin 0.8s linear infinite' }} /> {isCL ? 'Tailoring letter…' : 'Tailoring resume…'}</>
          : <><Sparkles size={15} /> Tailor Now</>}
      </button>

      {/* Success state */}
      {tailored && !loading && (
        <div style={{ marginTop: 12, padding: '13px 15px', background: '#ECFDF5', border: '1px solid #BBF7D0', borderRadius: 10, display: 'flex', alignItems: 'center', gap: 10 }}>
          <CheckCircle2 size={20} color="#16A34A" strokeWidth={2} style={{ flexShrink: 0 }} />
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#14532D' }}>{isCL ? 'Cover letter tailored!' : 'Resume tailored successfully!'}</div>
            <div style={{ fontSize: 12, color: '#166534', marginTop: 2 }}>Check the live preview to see the updates.</div>
          </div>
        </div>
      )}

      {/* Divider */}
      <div style={{ height: 1, background: '#F3F4F6', margin: '20px 0 16px' }} />

      {/* Tips */}
      <div style={{ fontSize: 11, fontWeight: 700, color: '#9CA3AF', letterSpacing: 0.6, textTransform: 'uppercase', marginBottom: 12 }}>How it works</div>
      {tips.map((tip, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 10 }}>
          <div style={{ width: 20, height: 20, borderRadius: 6, background: 'linear-gradient(135deg,#FFF1E8,#FFE7D6)', border: '1px solid #FED7AA', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>
            <span style={{ fontSize: 10, fontWeight: 700, color: '#F97316' }}>{i + 1}</span>
          </div>
          <span style={{ fontSize: 12, color: '#6B7280', lineHeight: 1.6 }}>{tip}</span>
        </div>
      ))}
    </div>
  );
};

// ── Main Builder ──────────────────────────────────────────────────────────────
export const BuilderPage = () => {
  const { type, id }  = useParams();
  const navigate      = useNavigate();
  const location      = useLocation();
  const { user, refreshUser } = useAuth();
  const { generateResume, tailorResume, analyzeATS, loading: aiLoading, dailyLimitError, clearDailyLimitError, improveSection, sectionLoading } = useClaudeAI();
  const { showAlert } = useDialog();

  const [data,        setData]       = useState(null);
  const [docId,       setDocId]      = useState(null);
  const [tab,         setTab]        = useState('edit');
  const [showAI,      setShowAI]     = useState(false);
  const [saving,      setSaving]     = useState(false);
  const [saved,       setSaved]      = useState(false);
  const [exporting,        setExporting]       = useState(false);
  const [exportOpen,       setExportOpen]      = useState(false);
  const [premiumSections,  setPremiumSections] = useState([]);
  const [showUpgrade,      setShowUpgrade]     = useState(false);
  const [showSignupPrompt, setShowSignupPrompt] = useState(false);
  const [platformFlags,    setPlatformFlags]   = useState({ downloadOnFree: true });

  // Guest mode: no logged-in user (public /build/:type route, no :id param)
  const isGuest = !user;

  // Pro = plan is 'pro' AND not expired (covers admin downgrade + plan expiry)
  const isPro = user?.plan === 'pro' &&
    (!user?.planExpiresAt || new Date(user.planExpiresAt) > new Date());

  // Refresh user plan from server on mount — catches admin downgrades and expired plans
  useEffect(() => { if (user) refreshUser().catch(() => {}); }, []);

  // Fetch which sections are premium
  useEffect(() => {
    api.getSectionConfig()
      .then(d => setPremiumSections(d.premiumSections || []))
      .catch(() => {});
  }, []);

  // Fetch platform flags to enforce download restrictions
  useEffect(() => {
    api.getPlatformFlags()
      .then(d => setPlatformFlags(d))
      .catch(() => {});
  }, []);

  // Blur preview when device switches away (screenshot attempt on mobile)
  useEffect(() => {
    const el = () => {
      const node = document.getElementById('resume-preview');
      if (!node) return;
      node.style.filter = document.visibilityState === 'hidden' ? 'blur(20px)' : 'none';
    };
    document.addEventListener('visibilitychange', el);
    return () => document.removeEventListener('visibilitychange', el);
  }, []);

  const hasMeaningfulValue = (value) => {
    if (value == null) return false;
    if (typeof value === 'string') return value.trim().length > 0;
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return Number.isFinite(value);
    if (Array.isArray(value)) return value.some(hasMeaningfulValue);
    if (typeof value === 'object') return Object.values(value).some(hasMeaningfulValue);
    return false;
  };

  // Check if user has any premium sections with content
  const sectionHasContent = (id) => {
    if (id === 'references' && data?.referencesAvailableOnRequest) return true;
    const val = data?.[id];
    return hasMeaningfulValue(val);
  };
  const usedPremiumSections = premiumSections.filter(sectionHasContent);
  const isLocked = !isPro && usedPremiumSections.length > 0;

  // Get template from query param (when coming from templates page)
  const queryTemplate = new URLSearchParams(location.search).get('template');

  useEffect(() => {
    const init = async () => {
      if (id === undefined) {
        // Guest builder (/build/:type, no doc id) — resume a locally saved draft if one matches this type
        const draftRaw = localStorage.getItem('mars_guest_draft');
        if (draftRaw) {
          try {
            const draft = JSON.parse(draftRaw);
            if (draft?.type === type && draft?.data) {
              setData(queryTemplate ? { ...draft.data, template: queryTemplate } : draft.data);
              return;
            }
          } catch {}
        }
        const empty = JSON.parse(JSON.stringify({ ...(EMPTIES[type] || EMPTY_RESUME), id: Date.now().toString() }));
        if (queryTemplate) empty.template = queryTemplate;
        setData(empty);
        return;
      }
      if (id === 'new' || id === 'ai') {
        /* If arriving from the job search tailor flow, load the tailored data directly */
        if (location.state?.tailoredData) {
          const td = location.state.tailoredData;
          setData({ ...td, id: td.id || Date.now().toString() });
          return;
        }
        let empty = JSON.parse(JSON.stringify({ ...(EMPTIES[type] || EMPTY_RESUME), id: Date.now().toString() }));
        if (queryTemplate) empty.template = queryTemplate;
        if (profileHasDetails(user?.userProfile || {})) {
          empty = applyProfileToDocument(empty, user.userProfile, user, type);
        }
        setData(empty);
        if (id === 'ai') setShowAI(true);
      } else {
        try {
          const doc = await api.getDoc(id);
          setData(doc.data || EMPTIES[type]);
          setDocId(doc._id);
        } catch {
          setData({ ...(EMPTIES[type] || EMPTY_RESUME), id: Date.now().toString() });
        }
      }
    };
    init();
  }, [type, id]);

  const handleSave = async (silent = false) => {
    if (isGuest) {
      localStorage.setItem('mars_guest_draft', JSON.stringify({ type, data, updatedAt: Date.now() }));
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      return;
    }
    setSaving(true);
    try {
      const payload = { type, title: data?.personalInfo?.fullName || 'Untitled', template: data?.template || 'modern', data };
      if (docId) {
        await api.updateDoc(docId, payload);
      } else {
        const created = await api.createDoc(payload);
        setDocId(created._id);
        track('document_created', { doc_type: type, template: payload.template });
        window.history.replaceState(null, '', `/builder/${type}/${created._id}`);
      }
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (err) { if (!silent) showAlert('Save failed: ' + err.message, { type: 'error', title: 'Save Failed' }); }
    finally { setSaving(false); }
  };

  // Autosave: once the doc exists (or we're in guest mode, saving locally), persist
  // changes ~2.5s after the user stops editing
  const saveRef = useRef(handleSave);
  saveRef.current = handleSave;
  const autosaveArmed = useRef(false);
  useEffect(() => {
    if (!data || (!docId && !isGuest)) return;
    if (!autosaveArmed.current) { autosaveArmed.current = true; return; } // skip initial load
    const t = setTimeout(() => saveRef.current(true), 2500);
    return () => clearTimeout(t);
  }, [data, docId]);

  const handleAIGenerate = async (input, jobDesc) => {
    try {
      const generated = await generateResume({ description: input, jobDescription: jobDesc }, type);
      setData(prev => ({ ...prev, ...generated, type, template: prev?.template || 'modern' }));
      setShowAI(false);
    } catch (e) { showAlert('AI error: ' + e.message, { type: 'error', title: 'AI Error' }); }
  };

  const handleTailor = async (jd) => {
    try {
      const tailored = await tailorResume(data, jd);
      setData(prev => ({ ...prev, ...tailored }));
    } catch (e) {
      showAlert('Tailoring failed: ' + e.message, { type: 'error', title: 'AI Error' });
      throw e;
    }
  };

  const handleAnalyzeATS = async (resumeData) => {
    try {
      return await analyzeATS(resumeData);
    } catch (e) {
      showAlert('ATS analysis failed: ' + e.message, { type: 'error', title: 'Analysis Error' });
      throw e;
    }
  };

  const handleImproveSection = async (resumeData, sectionKey, issues) => {
    try {
      return await improveSection(resumeData, sectionKey, issues);
    } catch (e) {
      if (e?.name !== 'DailyLimitError') showAlert('AI improvement failed: ' + e.message, { type: 'error', title: 'AI Error' });
      throw e;
    }
  };

  const checkLocked = async () => {
    // Re-fetch fresh user plan to catch mid-session downgrades / expiry
    let freshUser = user;
    try { freshUser = await refreshUser(); } catch {}
    const planActive = freshUser?.plan === 'pro' &&
      (!freshUser?.planExpiresAt || new Date(freshUser.planExpiresAt) > new Date());
    if (planActive) return false;

    // Re-fetch section config to get latest premium list
    try {
      const cfg = await api.getSectionConfig();
      const sects = cfg.premiumSections || [];
      setPremiumSections(sects);
      return sects.some(sectionHasContent);
    } catch {
      return usedPremiumSections.length > 0;
    }
  };

  const handleExportPDF = async () => {
    setExportOpen(false);
    if (isGuest) { setShowSignupPrompt(true); return; }
    if (!isPro && !platformFlags.downloadOnFree) { setShowUpgrade(true); return; }
    if (await checkLocked()) { setShowUpgrade(true); return; }
    setExporting(true);
    try {
      await exportToPDF('resume-preview', `${data?.personalInfo?.fullName || 'resume'}-${type}`, { pageSize: data?.personalInfo?.pageSize });
      track('document_exported', { format: 'pdf', doc_type: type, template: data?.template || '' });
    } finally {
      setExporting(false);
    }
  };

const handleExportTxt = () => {
    setExportOpen(false);
    if (isGuest) { setShowSignupPrompt(true); return; }
    if (!isPro && !platformFlags.downloadOnFree) { setShowUpgrade(true); return; }
    exportToTxt(data, `${data?.personalInfo?.fullName || 'resume'}-${type}`);
    track('document_exported', { format: 'txt', doc_type: type, template: data?.template || '' });
  };

  const handleShareLinkedIn = async () => {
    setExportOpen(false);
    if (isGuest) { setShowSignupPrompt(true); return; }
    if (!isPro) { setShowUpgrade(true); return; }
    const pi = data?.personalInfo || {};
    const name = pi.fullName || 'resume';
    const pdfFilename = `${name}-${type}`;

    // Download the PDF first so the user can attach it to the LinkedIn post.
    setExporting(true);
    try {
      await exportToPDF('resume-preview', pdfFilename, { pageSize: pi.pageSize });
    } finally {
      setExporting(false);
    }

    // Open LinkedIn post creation page — LinkedIn doesn't support programmatic
    // file attachments via URL, so the user attaches the downloaded PDF manually.
    const text = encodeURIComponent(
      `${name ? `${name} — ` : ''}${pi.jobTitle ? `${pi.jobTitle} | ` : ''}Open to new opportunities! Built with MarsResume.`
    );
    window.open(`https://www.linkedin.com/feed/?shareActive=true&text=${text}`, '_blank', 'noopener,noreferrer');
    track('document_shared', { platform: 'linkedin', doc_type: type });
  };

  const handleShareGmail = async () => {
    setExportOpen(false);
    if (isGuest) { setShowSignupPrompt(true); return; }
    if (!isPro) { setShowUpgrade(true); return; }
    const pi = data?.personalInfo || {};
    const name = pi.fullName || 'My Resume';
    const pdfFilename = `${name}-${type}`;

    // Download the PDF first so the user can attach it to the email.
    setExporting(true);
    try {
      await exportToPDF('resume-preview', pdfFilename, { pageSize: pi.pageSize });
    } finally {
      setExporting(false);
    }

    // Open Gmail compose pre-filled with subject, body, and an instruction
    // to attach the PDF that was just downloaded.
    const title = pi.jobTitle ? ` – ${pi.jobTitle}` : '';
    const contact = [pi.email, pi.phone, pi.location, pi.linkedin].filter(Boolean).join(' | ');
    const subject = encodeURIComponent(`${name}${title}`);
    const body = encodeURIComponent(
      `Hi,\n\nPlease find my resume attached (${pdfFilename}.pdf — just downloaded to your device).\n\n${name}${pi.jobTitle ? `\n${pi.jobTitle}` : ''}${contact ? `\n${contact}` : ''}\n\nBest regards,\n${name}`
    );
    window.open(`https://mail.google.com/mail/?view=cm&fs=1&su=${subject}&body=${body}`, '_blank', 'noopener,noreferrer');
    track('document_shared', { platform: 'gmail', doc_type: type });
  };

  const typeLabel = { resume: 'Resume', cover_letter: 'Cover Letter', cv: 'Academic CV' }[type] || 'Resume';
  const profileAvailable = profileHasDetails(user?.userProfile || {});
  const handleApplyProfile = () => {
    setData(prev => applyProfileToDocument(prev, user.userProfile, user, type));
  };
  const handleRemoveProfile = () => {
    setData(prev => clearProfileFromDocument(prev, type));
  };

  if (!data) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#eef1f6' }}>
      <RocketLoader size={200} message="Loading editor…" />
    </div>
  );

  const isCL = type === 'cover_letter';

  // Left panel content based on tab
  const renderLeftPanel = () => {
    if (tab === 'template') return <TemplatePicker current={data?.template || (isCL ? 'cl_classic' : 'modern')} onChange={t => { track('template_selected', { template: t, doc_type: type }); setData({ ...data, template: t }); }} data={data} type={type} />;
    if (tab === 'design')   return isCL ? <CLDesignPanel data={data} onChange={setData} /> : <DesignPanel data={data} onChange={setData} />;
    if (tab === 'tailor')   return <TailorPanel onTailor={handleTailor} loading={aiLoading} type={type} />;
    if (tab === 'ats')      return isCL
      ? (
        <div style={{ padding: '40px 20px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', height: '100%' }}>
          <div style={{ width: 64, height: 64, borderRadius: 16, background: '#F3F4F6', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
            <BarChart2 size={28} color="#9CA3AF" />
          </div>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#111827', marginBottom: 8 }}>ATS Score not available</div>
          <p style={{ margin: 0, fontSize: 13, color: '#6B7280', lineHeight: 1.65, maxWidth: 220 }}>
            ATS scoring is designed for resumes. Cover letters are typically read by humans, not parsed by ATS software.
          </p>
        </div>
      )
      : <ATSPanel data={data} onAnalyze={handleAnalyzeATS} loading={aiLoading} onImprove={handleImproveSection} sectionLoading={sectionLoading} onUpdate={setData} />;
    return (
      <div style={{ padding: 16 }}>
        <ProfileAutofillBar
          applied={!!data.profileAutofillApplied}
          available={profileAvailable}
          onApply={handleApplyProfile}
          onRemove={handleRemoveProfile}
        />
        <ResumeEditor data={data} onChange={setData} isPro={isPro} isGuest={isGuest} onRequireSignup={() => setShowSignupPrompt(true)} />
      </div>
    );
  };

  return (
    <div className={(isLocked || isGuest) ? 'builder-page builder-export-locked' : 'builder-page'} style={{ minHeight: '100vh', background: '#EFF2F7', display: 'flex', flexDirection: 'column', fontFamily: 'Inter, sans-serif', overflowX: 'hidden' }} onClick={() => exportOpen && setExportOpen(false)}>
      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }

        /* ── Premium lock: block browser print/save-as-PDF ── */
        @media print {
          .builder-export-locked * { visibility: hidden !important; }
          .builder-export-locked .builder-print-lock { visibility: visible !important; display: flex !important; position: fixed; inset: 0; align-items: center; justify-content: center; background: white; flex-direction: column; gap: 16px; }
          .builder-export-locked .builder-print-lock * { visibility: visible !important; }
        }

        /* ── Builder responsive ── */
        .builder-topbar { flex-wrap: nowrap; }
        .builder-topbar-center { scrollbar-width: none; }
        .builder-topbar-center::-webkit-scrollbar { display: none; }

        @media (max-width: 1024px) {
          .builder-topbar-center { overflow-x: auto !important; flex-shrink: 1 !important; }
          .builder-live-preview { padding: 12px !important; }
        }

        @media (max-width: 860px) {
          .builder-topbar { height: auto !important; flex-wrap: wrap !important; padding: 8px 12px 0 !important; gap: 6px !important; }
          .builder-topbar-left { flex: 1 1 auto !important; }
          .builder-topbar-right { flex: 0 0 auto !important; }
          .builder-topbar-center { order: 10; width: 100% !important; padding: 6px 0 8px !important; border-top: 1px solid #F3F4F6; overflow-x: auto !important; }
          .builder-topbar-center > div { min-width: max-content !important; }
          .builder-split { flex-direction: column !important; height: auto !important; min-height: calc(100vh - 110px) !important; overflow-x: hidden !important; overflow-y: visible !important; }
          .builder-left-panel { width: 100% !important; max-width: 100vw !important; max-height: none !important; overflow-x: hidden !important; overflow-y: auto !important; border-right: none !important; border-bottom: 1px solid #E5E7EB !important; flex-shrink: 0 !important; }
          .builder-live-preview { display: none !important; }
          .builder-doc-icon { display: none !important; }
          .builder-divider { display: none !important; }
          .builder-sample-btn { display: none !important; }
        }

        @media (max-width: 540px) {
          .builder-topbar { padding: 8px 10px 0 !important; }
          .builder-topbar-right { gap: 4px !important; }
          .builder-ai-btn span { display: none !important; }
          .builder-save-btn span { display: none !important; }
          .builder-topbar-left { max-width: calc(100vw - 116px) !important; overflow: hidden; }
          .builder-ai-btn, .builder-save-btn { padding: 8px 10px !important; }
        }
        @media (max-width: 400px) {
          .builder-topbar-right .export-btn-text { display: none !important; }
          .builder-topbar-center button { font-size: 11px !important; padding: 0 8px !important; }
        }
        @media (max-width: 360px) {
          .builder-topbar-right { gap: 3px !important; }
          .builder-ai-btn, .builder-save-btn { padding: 7px 8px !important; }
        }

      `}</style>

      {/* ── Top Bar ── */}
      <div className="builder-topbar" style={{ background: 'white', borderBottom: '1px solid #E5E7EB', padding: '0 20px', display: 'flex', alignItems: 'stretch', justifyContent: 'space-between', height: 64, position: 'sticky', top: 0, zIndex: 10, gap: 0, boxShadow: '0 2px 12px rgba(0,0,0,0.07)' }}>

        {/* Left: back */}
        <div className="builder-topbar-left" style={{ display: 'flex', alignItems: 'center', paddingRight: 20, borderRight: '1px solid #F0F0F0', flex: '0 0 auto' }}>
          <button onClick={() => window.history.state?.idx > 0 ? navigate(-1) : navigate('/dashboard')}
            style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'transparent', border: '1.5px solid #E5E7EB', color: '#374151', cursor: 'pointer', fontSize: 13, borderRadius: 8, padding: '7px 14px', fontWeight: 600, fontFamily: 'Inter, sans-serif', flexShrink: 0, transition: 'all 0.15s' }}
            onMouseEnter={e => { e.currentTarget.style.background = '#F9FAFB'; e.currentTarget.style.borderColor = '#D1D5DB'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = '#E5E7EB'; }}>
            <ArrowLeft size={14} /> Back
          </button>
        </div>

        {/* Center: tab switcher */}
        <div className="builder-topbar-center" style={{ display: 'flex', flex: '1 1 auto', justifyContent: 'center', alignItems: 'stretch' }}>
          <div style={{ display: 'flex', alignItems: 'stretch', gap: 0 }}>
            <TabBtn active={tab === 'edit'}     onClick={() => setTab('edit')}     icon={Pencil}>Edit</TabBtn>
            <TabBtn active={tab === 'design'}   onClick={() => setTab('design')}   icon={Palette}>Design</TabBtn>
            <TabBtn active={tab === 'template'} onClick={() => setTab('template')} icon={LayoutTemplate}>Templates</TabBtn>
            <TabBtn active={tab === 'tailor'}   onClick={() => { if (isGuest) { setShowSignupPrompt(true); return; } if (!isPro) { setShowUpgrade(true); return; } setTab('tailor'); }}   icon={Target}>Tailor{!isPro && <Lock size={10} color="#9CA3AF" style={{ marginLeft: 2 }} />}</TabBtn>
            <TabBtn active={tab === 'ats'}      onClick={() => { if (isGuest) { setShowSignupPrompt(true); return; } if (!isPro) { setShowUpgrade(true); return; } setTab('ats'); }}      icon={BarChart2}>ATS Score{!isPro && <Lock size={10} color="#9CA3AF" style={{ marginLeft: 2 }} />}</TabBtn>
            <TabBtn active={tab === 'preview'}  onClick={() => setTab('preview')}  icon={Eye}>Preview</TabBtn>
          </div>
        </div>

        {/* Right: actions */}
        <div className="builder-topbar-right" style={{ display: 'flex', gap: 8, alignItems: 'center', flex: '0 0 auto', paddingLeft: 20, borderLeft: '1px solid #F0F0F0' }}>
          {/* AI */}
          <button className="builder-ai-btn" onClick={() => { if (isGuest) { setShowSignupPrompt(true); return; } setShowAI(true); }}
            style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'linear-gradient(135deg,#FB923C,#F97316)', color: 'white', border: 'none', borderRadius: 9, padding: '8px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'Inter, sans-serif', boxShadow: '0 2px 8px rgba(249,115,22,0.35)', letterSpacing: 0.2 }}>
            <Sparkles size={14} /><span>AI</span>
          </button>

          {/* Sample */}
          <button className="builder-sample-btn" onClick={() => {
            const tmpl = data?.template;
            if (type === 'cover_letter') { setData({ ...SAMPLE_CL, template: tmpl || 'cl_classic' }); return; }
            const t = tmpl || (type === 'cv' ? 'academic' : 'modern');
            const samplePhoto = PHOTO_TEMPLATES.has(t) ? '/assets/sample-profile.png' : null;
            setData({
              ...SAMPLE_RESUME,
              ...(type === 'cv' ? { type: 'cv' } : {}),
              template: t,
              personalInfo: { ...SAMPLE_RESUME.personalInfo, photo: samplePhoto },
            });
          }}
            style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'transparent', color: '#6B7280', border: '1.5px solid #E5E7EB', borderRadius: 9, padding: '7px 13px', fontSize: 13, fontWeight: 500, cursor: 'pointer', fontFamily: 'Inter, sans-serif', transition: 'all 0.15s' }}
            onMouseEnter={e => { e.currentTarget.style.background = '#F9FAFB'; e.currentTarget.style.color = '#374151'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#6B7280'; }}>
            <FileText size={14} /> Sample
          </button>

          {/* Save */}
          <button className="builder-save-btn" onClick={() => handleSave()} disabled={saving}
            style={{ display: 'flex', alignItems: 'center', gap: 6, background: saved ? '#D1FAE5' : saving ? '#D1FAE5' : 'linear-gradient(135deg, #16A34A, #15803D)', color: saved || saving ? '#065F46' : 'white', border: saved || saving ? '1.5px solid #A7F3D0' : 'none', borderRadius: 9, padding: '7px 14px', fontSize: 13, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', fontFamily: 'Inter, sans-serif', transition: 'all 0.2s', boxShadow: saved || saving ? 'none' : '0 2px 8px rgba(22,163,74,0.30)', opacity: saving ? 0.85 : 1 }}
            onMouseEnter={e => { if (!saving && !saved) e.currentTarget.style.boxShadow = '0 4px 14px rgba(22,163,74,0.42)'; }}
            onMouseLeave={e => { if (!saving && !saved) e.currentTarget.style.boxShadow = '0 2px 8px rgba(22,163,74,0.30)'; }}>
            {saving ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : saved ? <Check size={14} /> : <Save size={14} />}
            <span>{saving ? 'Saving…' : saved ? (isGuest ? 'Draft saved' : 'Saved!') : 'Save'}</span>
          </button>

          {/* Export: PDF/Word clicks are gated if a free user used premium sections */}
          <div style={{ position: 'relative' }} onClick={e => e.stopPropagation()}>
            <button onClick={() => setExportOpen(p => !p)} disabled={exporting}
              style={{ display: 'flex', alignItems: 'center', gap: 7, background: 'linear-gradient(135deg,#1F2937,#111827)', color: 'white', border: 'none', borderRadius: 9, padding: '8px 16px', fontSize: 13, fontWeight: 700, cursor: exporting ? 'not-allowed' : 'pointer', opacity: exporting ? 0.72 : 1, fontFamily: 'Inter, sans-serif', boxShadow: '0 2px 8px rgba(0,0,0,0.22)', letterSpacing: 0.2 }}>
              {exporting ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Download size={14} />}
              <span className="export-btn-text">{exporting ? 'Exporting…' : 'Export'}</span> <ChevronDown size={12} style={{ opacity: 0.7 }} />
            </button>
            {exportOpen && (
              <div style={{ position: 'absolute', right: 0, top: 'calc(100% + 6px)', background: 'white', border: '1px solid #E5E7EB', borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', overflow: 'hidden', minWidth: 160, zIndex: 50 }}>
                <button onClick={() => handleExportPDF()}
                  style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 9, padding: '11px 14px', background: 'transparent', border: 'none', fontSize: 13, color: '#111827', cursor: 'pointer', fontFamily: 'Inter, sans-serif', textAlign: 'left', fontWeight: 500 }}
                  onMouseEnter={e => e.currentTarget.style.background = '#F9FAFB'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <FileText size={14} color="#EF1D28" /> PDF
                </button>
                <div style={{ height: 1, background: '#F3F4F6' }} />
                <button onClick={() => handleExportTxt()}
                  style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 9, padding: '11px 14px', background: 'transparent', border: 'none', fontSize: 13, color: '#111827', cursor: 'pointer', fontFamily: 'Inter, sans-serif', textAlign: 'left', fontWeight: 500 }}
                  onMouseEnter={e => e.currentTarget.style.background = '#F9FAFB'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <FileText size={14} color="#059669" /> Plain Text (.txt)
                </button>

                {/* ── Share section (Pro only) ── */}
                <div style={{ height: 1, background: '#E5E7EB', margin: '4px 0' }} />
                <div style={{ padding: '5px 14px 3px', display: 'flex', alignItems: 'center', gap: 5 }}>
                  <Share2 size={10} color="#9CA3AF" />
                  <span style={{ fontSize: 10, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Share Resume</span>
                  {!isPro && <Lock size={10} color="#9CA3AF" style={{ marginLeft: 2 }} />}
                </div>
                <button onClick={handleShareLinkedIn}
                  style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 9, padding: '9px 14px', background: 'transparent', border: 'none', fontSize: 13, color: isPro ? '#111827' : '#9CA3AF', cursor: 'pointer', fontFamily: 'Inter, sans-serif', textAlign: 'left', fontWeight: 500 }}
                  onMouseEnter={e => e.currentTarget.style.background = '#F9FAFB'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <Linkedin size={14} color={isPro ? '#0A66C2' : '#9CA3AF'} /> LinkedIn
                  {!isPro && <span style={{ marginLeft: 'auto', fontSize: 10, color: '#9CA3AF', fontWeight: 600 }}>Pro</span>}
                </button>
                <div style={{ height: 1, background: '#F3F4F6' }} />
                <button onClick={handleShareGmail}
                  style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 9, padding: '9px 14px', background: 'transparent', border: 'none', fontSize: 13, color: isPro ? '#111827' : '#9CA3AF', cursor: 'pointer', fontFamily: 'Inter, sans-serif', textAlign: 'left', fontWeight: 500 }}
                  onMouseEnter={e => e.currentTarget.style.background = '#F9FAFB'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <Mail size={14} color={isPro ? '#EA4335' : '#9CA3AF'} /> Gmail
                  {!isPro && <span style={{ marginLeft: 'auto', fontSize: 10, color: '#9CA3AF', fontWeight: 600 }}>Pro</span>}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Content ── */}
      <div style={{ flex: 1, overflow: 'hidden', display: 'flex' }}>
        {tab === 'preview' ? (
          // Full preview mode
          <div style={{ flex: 1, overflow: 'auto', background: '#E2E8F0', padding: 'clamp(8px, 3vw, 28px)' }}>
            <div style={{ maxWidth: 800, margin: '0 auto', position: 'relative' }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: '#94A3B8', textAlign: 'center', marginBottom: 12, textTransform: 'uppercase', letterSpacing: 1 }}>Full Preview</div>
              <div>
                <ResumePreview data={data} watermark={isGuest} />
              </div>
            </div>
          </div>
        ) : (
          // Split: left panel + live preview
          <div className="builder-split" style={{ display: 'flex', flex: 1, height: 'calc(100vh - 60px)', overflow: 'hidden' }}>
            {/* Left Panel */}
            <div className="builder-left-panel" style={{ width: tab === 'design' ? '286px' : (tab === 'tailor' || tab === 'ats') ? '340px' : '46%', overflow: tab === 'template' ? 'hidden' : 'auto', display: tab === 'template' ? 'flex' : undefined, flexDirection: tab === 'template' ? 'column' : undefined, borderRight: '1px solid #E5E7EB', background: (tab === 'tailor' || tab === 'ats') ? 'white' : '#F9FAFB', flexShrink: 0 }}>
              {renderLeftPanel()}
            </div>
            {/* Live Preview */}
            <div className="builder-live-preview" style={{ flex: 1, overflow: 'auto', padding: 'clamp(8px, 2vw, 20px)', background: '#E2E8F0' }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: '#94A3B8', textAlign: 'center', marginBottom: 10, textTransform: 'uppercase', letterSpacing: 1 }}>Live Preview</div>
              <div style={{ maxWidth: 794, margin: '0 auto', position: 'relative' }}>
                <div>
                  <ResumePreview data={data} watermark={isGuest} />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {showAI && (
        <AIModal
          type={type}
          onGenerate={handleAIGenerate}
          onClose={() => setShowAI(false)}
          loading={aiLoading}
          initialInput={location.state?.aiSeed?.description || ''}
          initialJobDesc={location.state?.aiSeed?.jobContext || ''}
        />
      )}

      {/* Print-lock overlay (visible only when printing) */}
      <div className="builder-print-lock" style={{ display: 'none' }}>
        <div style={{ fontSize: 48 }}>🔒</div>
        <div style={{ fontSize: 22, fontWeight: 800, color: '#111827' }}>Export Restricted</div>
        <div style={{ fontSize: 14, color: '#6B7280', maxWidth: 340, textAlign: 'center' }}>
          {isGuest
            ? `Create a free account to export and download your ${typeLabel.toLowerCase()}.`
            : `Your ${typeLabel.toLowerCase()} uses premium sections. Switch to Pro to export and download.`}
        </div>
        <div style={{ fontSize: 13, color: '#EF1D28', fontWeight: 700 }}>{isGuest ? 'marsresume.com/signup' : 'marsresume.com/pricing'}</div>
      </div>

      {/* Daily AI limit modal */}
      {dailyLimitError && <DailyLimitModal onClose={clearDailyLimitError} />}

      {/* Upgrade modal */}
      {showUpgrade && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
          onClick={() => setShowUpgrade(false)}>
          <div style={{ background: 'white', borderRadius: 20, padding: '36px 32px', maxWidth: 420, width: '100%', textAlign: 'center', boxShadow: '0 20px 60px rgba(0,0,0,0.25)', position: 'relative' }}
            onClick={e => e.stopPropagation()}>
            <button onClick={() => setShowUpgrade(false)} style={{ position: 'absolute', top: 14, right: 16, background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#9CA3AF', lineHeight: 1 }}>×</button>
            <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'linear-gradient(135deg,#EF1D28,#F97316)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
              <Lock size={28} color="white" />
            </div>
            <div style={{ fontSize: 22, fontWeight: 800, color: '#111827', marginBottom: 8 }}>Switch to Pro to Download</div>
            <div style={{ fontSize: 14, color: '#6B7280', lineHeight: 1.6, marginBottom: 8 }}>
              Your {typeLabel.toLowerCase()} includes <strong style={{ color: '#111827' }}>{usedPremiumSections.length} premium section{usedPremiumSections.length > 1 ? 's' : ''}</strong>:
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, justifyContent: 'center', marginBottom: 20 }}>
              {usedPremiumSections.map(id => (
                <span key={id} style={{ background: '#FEF3C7', color: '#B45309', border: '1px solid #FDE68A', borderRadius: 20, padding: '3px 12px', fontSize: 12, fontWeight: 600 }}>
                  ⭐ {getPremiumSectionLabel(id)}
                </span>
              ))}
            </div>
            <div style={{ fontSize: 13, color: '#6B7280', lineHeight: 1.6, marginBottom: 24 }}>
              Switch to a <strong style={{ color: '#111827' }}>Pro account</strong> to download PDF files with premium sections.
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setShowUpgrade(false)} style={{ flex: 1, padding: '11px', border: '1.5px solid #E5E7EB', borderRadius: 10, background: 'white', color: '#374151', fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
                Keep Editing
              </button>
              <button onClick={() => { setShowUpgrade(false); navigate('/pricing'); }} style={{ flex: 1, padding: '11px', border: 'none', borderRadius: 10, background: 'linear-gradient(135deg,#EF1D28,#F97316)', color: 'white', fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', boxShadow: '0 4px 12px rgba(239,29,40,0.30)' }}>
                Switch to Pro
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Signup-required modal (guest builder) */}
      {showSignupPrompt && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
          onClick={() => setShowSignupPrompt(false)}>
          <div style={{ background: 'white', borderRadius: 20, padding: '36px 32px', maxWidth: 420, width: '100%', textAlign: 'center', boxShadow: '0 20px 60px rgba(0,0,0,0.25)', position: 'relative' }}
            onClick={e => e.stopPropagation()}>
            <button onClick={() => setShowSignupPrompt(false)} style={{ position: 'absolute', top: 14, right: 16, background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#9CA3AF', lineHeight: 1 }}>×</button>
            <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'linear-gradient(135deg,#EF1D28,#F97316)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
              <Lock size={28} color="white" />
            </div>
            <div style={{ fontSize: 22, fontWeight: 800, color: '#111827', marginBottom: 8 }}>Create a Free Account to Download</div>
            <div style={{ fontSize: 14, color: '#6B7280', lineHeight: 1.6, marginBottom: 24 }}>
              Your {typeLabel.toLowerCase()} is ready! Sign up free to remove the watermark preview and download it — your work is saved automatically, nothing is lost.
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setShowSignupPrompt(false)} style={{ flex: 1, padding: '11px', border: '1.5px solid #E5E7EB', borderRadius: 10, background: 'white', color: '#374151', fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
                Keep Editing
              </button>
              <button onClick={() => { handleSave(true); navigate('/signup?from=guest-builder'); }} style={{ flex: 1, padding: '11px', border: 'none', borderRadius: 10, background: 'linear-gradient(135deg,#EF1D28,#F97316)', color: 'white', fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', boxShadow: '0 4px 12px rgba(239,29,40,0.30)' }}>
                Sign Up Free
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
